"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Eye,
  ListChecks,
  ShieldCheck,
  Layers,
  GitBranch,
  Database,
  FileCode2,
  Plug,
  Rocket,
  TestTube2,
  FileText,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  BookOpen,
  Cpu,
  Globe,
  Lock,
  Zap,
  Search,
  Server,
  CreditCard,
  MessageSquare,
  Users,
  BarChart3,
  Tag,
  Clock,
  Shield,
  Smartphone,
  Settings,
  PenTool,
  GraduationCap,
  QrCode,
  Scale,
  Key,
  Wallet,
  Brain,
  Mic,
  HardDrive,
  Radio,
  Activity,
  AlertTriangle,
  RefreshCw,
  Hash,
  FileCheck,
  MapPin,
  TrendingUp,
} from "lucide-react";

// ---------------------------------------------------------------------------
// DATA
// ---------------------------------------------------------------------------

const STATS = [
  { label: "Requisitos", value: "160" },
  { label: "Removidos", value: "0" },
  { label: "Tabelas", value: "135" },
  { label: "Rotas API", value: "158" },
  { label: "Testes", value: "38" },
  { label: "ADRs", value: "6" },
];

const SECTIONS = [
  { id: "visao", title: "Visão do Produto", icon: Eye },
  { id: "livro-ape", title: "Livro APE", icon: BookOpen },
  { id: "cursos", title: "Cursos", icon: GraduationCap },
  { id: "entrevista", title: "Entrevista Jhow", icon: MessageSquare },
  { id: "passaporte-gen", title: "Gerador de Passaporte", icon: MapPin },
  { id: "planos", title: "Planos", icon: Wallet },
  { id: "precificacao", title: "Precificação", icon: CreditCard },
  { id: "funil", title: "Funil Evergreen", icon: TrendingUp },
  { id: "checkout", title: "Checkout Transparente", icon: CreditCard },
  { id: "requisitos", title: "Requisitos Funcionais", icon: ListChecks },
  { id: "nao-funcionais", title: "Requisitos Não-Funcionais", icon: ShieldCheck },
  { id: "arquitetura", title: "Arquitetura", icon: Layers },
  { id: "fluxos", title: "Fluxos de Usuário", icon: GitBranch },
  { id: "dados", title: "Modelo de Dados", icon: Database },
  { id: "api", title: "API Contract", icon: FileCode2 },
  { id: "integracoes", title: "Integrações", icon: Plug },
  { id: "deploy", title: "Plano de Deploy", icon: Rocket },
  { id: "testes", title: "Plano de Testes", icon: TestTube2 },
  { id: "adrs", title: "ADRs", icon: FileText },
  { id: "maquina-livros", title: "Máquina de Livros", icon: PenTool },
  { id: "passo1", title: "Gerador de Passaporte (SDD)", icon: FileCheck },
  { id: "auth-onboarding", title: "Auth / Onboarding", icon: Key },
];

const VALUE_PROPS = [
  {
    icon: BookOpen,
    title: "Metodologia científica operacionalizada",
    desc: "12 pesquisadores transformados em fluxos step-by-step que qualquer expert consegue seguir.",
  },
  {
    icon: Cpu,
    title: "IA como co-autora",
    desc: "Sugestões inteligentes que o expert aceita, edita ou refina. O humano sempre decide.",
  },
  {
    icon: RefreshCw,
    title: "Multi-LLM resiliente",
    desc: "4 provedores com fallback automático. O aluno nunca ve erro técnico.",
  },
  {
    icon: FileCheck,
    title: "Documentos prontos",
    desc: "Cada fluxo gera um documento versionado e compartilhavel, pronto para uso.",
  },
];

const BUSINESS_MODEL = [
  {
    item: "Produtos digitais (cursos, livros, eventos)",
    detail: "Arquitetura sales_channels + products, checkout transparente",
    provider: "Assas",
  },
  {
    item: "Acesso Completo (plano unico)",
    detail: "R$ 19,90 / 7 dias com acesso a todos os recursos",
    provider: "Assas",
  },
  {
    item: "Pacotes de moedas (credit packs)",
    detail: "Compra avulsa de moedas, precos carregados do banco",
    provider: "Assas",
  },
  {
    item: "Trial 24h via SMS",
    detail: "20.000 moedas, verificacao anti-fraude por telefone",
    provider: "Twilio",
  },
  {
    item: "Cupons promocionais",
    detail: "Codigos resgataveis para creditos bonus",
    provider: "Interno",
  },
  {
    item: "WhatsApp multi-instancia",
    detail: "Inbox, broadcasts, bots, AI agents, sequencias",
    provider: "Evolution API",
  },
  {
    item: "Rastreamento Meta CAPI",
    detail: "Conversions API server-side para otimizacao de anuncios",
    provider: "Meta",
  },
];

const PLANS = [
  {
    name: "Acesso Completo",
    price: "R$ 19,90",
    period: "/ 7 dias",
    credits: "Acesso a plataforma + moedas inclusas",
    highlight: true,
    color: "violet",
  },
  {
    name: "Pacotes de Creditos",
    price: "Variavel",
    period: "",
    credits: "Configuraveis pelo admin (credit_packs)",
    color: "violet",
  },
  {
    name: "Produtos Avulsos",
    price: "Variavel",
    period: "",
    credits: "Livros, cursos, eventos (via sales_channels)",
    color: "violet",
  },
];

interface Requirement {
  id: string;
  desc: string;
}

interface FunctionalModule {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  range: string;
  items: Requirement[];
}

const FUNCTIONAL_MODULES: FunctionalModule[] = [
  {
    title: "Autenticacao",
    icon: Lock,
    range: "RF-001 a RF-008",
    items: [
      { id: "RF-001", desc: "Registro via email/senha com confirmacao" },
      { id: "RF-002", desc: "Registro via Google OAuth" },
      { id: "RF-003", desc: "Login persistente com sessao" },
      { id: "RF-004", desc: "Recuperacao de senha por email" },
      { id: "RF-005", desc: "Confirmacao de email obrigatoria" },
      { id: "RF-006", desc: "Honeypot anti-bot no registro" },
      { id: "RF-007", desc: "Edicao de perfil (nome, avatar)" },
      { id: "RF-008", desc: "Sistema de roles (user, admin, super_admin)" },
    ],
  },
  {
    title: "Gerador de Passaporte",
    icon: MapPin,
    range: "RF-009 a RF-020",
    items: [
      { id: "RF-009", desc: "Input multi-formato (texto livre, PDF, entrevista Jhow)" },
      { id: "RF-010", desc: "Geracao automatica de Passaporte de Contexto via IA (11 secoes)" },
      { id: "RF-011", desc: "Renderizacao visual do passaporte com temas configuraveis" },
      { id: "RF-012", desc: "Geracao automatica de 6 Segmentos de mercado" },
      { id: "RF-013", desc: "Personas on-demand por segmento via IA" },
      { id: "RF-014", desc: "Upload de foto do expert" },
      { id: "RF-015", desc: "Export como Markdown" },
      { id: "RF-016", desc: "Sistema de referencias para aprendizado de formato (few-shot)" },
      { id: "RF-017", desc: "Consumo de moedas proporcional a tokens usados" },
      { id: "RF-018", desc: "Slug inteligente (nome + area, fallback random se colisao)" },
      { id: "RF-019", desc: "Temas visuais (teal, emerald, blue, violet, amber, rose)" },
      { id: "RF-020", desc: "Integracao com biblioteca (user_documents)" },
    ],
  },
  {
    title: "Entrevista Jhow",
    icon: MessageSquare,
    range: "RF-021 a RF-030",
    items: [
      { id: "RF-021", desc: "Chat interativo com personagem Jhow (IA)" },
      { id: "RF-022", desc: "4 fases: onboarding, orientacao, entrevista, conclusao" },
      { id: "RF-023", desc: "Geracao de expert_summary estruturado" },
      { id: "RF-024", desc: "Transcricao de audio via Groq Whisper" },
      { id: "RF-025", desc: "Upload de arquivos de referencia" },
      { id: "RF-026", desc: "Guardrails de progressao entre fases" },
      { id: "RF-027", desc: "Guardrails de tamanho de mensagens" },
      { id: "RF-028", desc: "Validacao de resumo pelo usuario" },
      { id: "RF-029", desc: "Geracao de documento pos-entrevista" },
      { id: "RF-030", desc: "Opcao de refazer entrevista" },
    ],
  },
  {
    title: "Biblioteca de Documentos",
    icon: FileText,
    range: "RF-031 a RF-038",
    items: [
      { id: "RF-031", desc: "Listagem de passaportes, personas e certificados" },
      { id: "RF-032", desc: "Versionamento automatico" },
      { id: "RF-033", desc: "Compartilhamento via token unico" },
      { id: "RF-034", desc: "Revogacao de compartilhamento" },
      { id: "RF-035", desc: "Acesso a versoes anteriores" },
      { id: "RF-036", desc: "Vinculacao parent-child (passaporte -> segmentos -> personas)" },
      { id: "RF-037", desc: "Export como Markdown" },
      { id: "RF-038", desc: "Tipos: passaporte, persona, certificado, livro" },
    ],
  },
  {
    title: "Creditos / Moedas",
    icon: CreditCard,
    range: "RF-039 a RF-048",
    items: [
      { id: "RF-039", desc: "Consulta de saldo em tempo real" },
      { id: "RF-040", desc: "Historico de transacoes de moedas" },
      { id: "RF-041", desc: "Compra de pacotes de moedas (credit_packs)" },
      { id: "RF-042", desc: "Transacoes de moedas com audit trail" },
      { id: "RF-043", desc: "Resgate de cupom pelo usuario" },
      { id: "RF-044", desc: "Creditos de boas-vindas no registro (idempotente)" },
      { id: "RF-045", desc: "Ajuste manual por admin com justificativa" },
      { id: "RF-046", desc: "Concessao automatica por plano" },
      { id: "RF-047", desc: "Cupons: tipos one_time, limited, unlimited" },
      { id: "RF-048", desc: "Anti-fraude: UNIQUE constraint por usuario por cupom" },
    ],
  },
  {
    title: "Trial 24h SMS",
    icon: Clock,
    range: "RF-049 a RF-058",
    items: [
      { id: "RF-049", desc: "Envio de codigo SMS (6 digitos) via Twilio" },
      { id: "RF-050", desc: "Verificacao com max 5 tentativas" },
      { id: "RF-051", desc: "Expiracao de codigo em 10 minutos" },
      { id: "RF-052", desc: "Rate limit: 3 codigos/telefone/hora" },
      { id: "RF-053", desc: "Anti-fraude: 1 telefone = 1 trial (hash SHA-256)" },
      { id: "RF-054", desc: "Normalizacao telefone E.164 (+55)" },
      { id: "RF-055", desc: "Ativacao atomica de trial 24h + 20.000 moedas" },
      { id: "RF-056", desc: "Banner de trial ativo no dashboard" },
      { id: "RF-057", desc: "Conversao de trial para assinatura" },
      { id: "RF-058", desc: "Auto-expiracao apos 24 horas" },
    ],
  },
  {
    title: "Cursos",
    icon: GraduationCap,
    range: "RF-059 a RF-072",
    items: [
      { id: "RF-059", desc: "Estrutura: cursos > modulos > aulas" },
      { id: "RF-060", desc: "9 cursos no catalogo (APE, Vibecoding PRO, Pre-Vibecoding, etc)" },
      { id: "RF-061", desc: "Player Spotify-like com sidebar de navegacao" },
      { id: "RF-062", desc: "Progresso automatico por aula" },
      { id: "RF-063", desc: "Controle de acesso por curso" },
      { id: "RF-064", desc: "Sistema de comentarios por aula" },
      { id: "RF-065", desc: "Sistema de avaliacoes (ratings)" },
      { id: "RF-066", desc: "Certificados de conclusao" },
      { id: "RF-067", desc: "Audio premium Google TTS Chirp3-HD" },
      { id: "RF-068", desc: "Aulas gratuitas (is_free)" },
      { id: "RF-069", desc: "Admin: gestao de cursos e conteudo" },
      { id: "RF-070", desc: "Admin: moderacao de comentarios" },
      { id: "RF-071", desc: "Completions tracking por usuario" },
      { id: "RF-072", desc: "Landing pages por curso" },
    ],
  },
  {
    title: "Maquina de Livros",
    icon: PenTool,
    range: "RF-073 a RF-090",
    items: [
      { id: "RF-073", desc: "CRUD de projetos de livro" },
      { id: "RF-074", desc: "Editor de capitulos com rich-text" },
      { id: "RF-075", desc: "Sistema de revisoes por capitulo" },
      { id: "RF-076", desc: "Build: preview digital (HTML)" },
      { id: "RF-077", desc: "Build: versao impressa (PDF)" },
      { id: "RF-078", desc: "Build: versao digital (PDF)" },
      { id: "RF-079", desc: "Build: EPUB para e-readers" },
      { id: "RF-080", desc: "Build: KDP (Amazon Kindle)" },
      { id: "RF-081", desc: "Audio chapters via Google TTS" },
      { id: "RF-082", desc: "Upload de capas" },
      { id: "RF-083", desc: "Sistema de pedidos (book_orders)" },
      { id: "RF-084", desc: "Checkout com endereco de entrega" },
      { id: "RF-085", desc: "Integracao Assas para pagamento" },
      { id: "RF-086", desc: "Email de entrega pos-compra" },
      { id: "RF-087", desc: "Metadados editoriais (ISBN, sinopse)" },
      { id: "RF-088", desc: "Diagramacao automatizada" },
      { id: "RF-089", desc: "Podcast a partir de capitulos" },
      { id: "RF-090", desc: "Versionamento de builds" },
    ],
  },
  {
    title: "WhatsApp",
    icon: Smartphone,
    range: "RF-091 a RF-115",
    items: [
      { id: "RF-091", desc: "Multi-instancia (Baileys + Cloud API via Evolution)" },
      { id: "RF-092", desc: "Inbox unificado com filtros e busca" },
      { id: "RF-093", desc: "Contatos como entidade de primeira classe" },
      { id: "RF-094", desc: "Auto-respostas (keyword, regex, first_message, menu)" },
      { id: "RF-095", desc: "Broadcasts com lock atomico e pause/resume" },
      { id: "RF-096", desc: "Sequencias automatizadas (drip campaigns)" },
      { id: "RF-097", desc: "AI Agent com system prompt + historico thread" },
      { id: "RF-098", desc: "Gatilhos por evento (webhook personalizado por trigger)" },
      { id: "RF-099", desc: "Politicas de grupo (allowlist)" },
      { id: "RF-100", desc: "Media em Supabase Storage (proxy 3-tier)" },
      { id: "RF-101", desc: "Voice notes (OGG/Opus)" },
      { id: "RF-102", desc: "Reply/quote com scroll-to-original" },
      { id: "RF-103", desc: "Templates HSM: CRUD + aprovacao Meta" },
      { id: "RF-104", desc: "Atribuicao de contato a atendente" },
      { id: "RF-105", desc: "Status de conversa: open/pending/resolved/snoozed" },
      { id: "RF-106", desc: "Toggle automacoes por contato" },
      { id: "RF-107", desc: "AI Agent: handover keyword pra humano" },
      { id: "RF-108", desc: "AI Agent: cap de mensagens + cost tracking" },
      { id: "RF-109", desc: "Nova inbox: Meta Embedded Signup + Baileys QR" },
      { id: "RF-110", desc: "Settings: abas Geral, Conexao, Mensagens, Health, API" },
      { id: "RF-111", desc: "HMAC Meta X-Hub-Signature-256 no webhook" },
      { id: "RF-112", desc: "Auth Evolution via header apikey" },
      { id: "RF-113", desc: "Rate limit Upstash: admin 20/min, broadcast 10/h" },
      { id: "RF-114", desc: "Audit log append-only com viewer paginado" },
      { id: "RF-115", desc: "Broadcast KPIs (Total/Sent/Delivered/Read/Failed)" },
    ],
  },
  {
    title: "Blog / SEO",
    icon: Globe,
    range: "RF-116 a RF-125",
    items: [
      { id: "RF-116", desc: "Editor rich-text com Tiptap v2" },
      { id: "RF-117", desc: "Categorias de artigo" },
      { id: "RF-118", desc: "Web Stories (formato Google)" },
      { id: "RF-119", desc: "CTAs inline automaticos (livro APE + Mapa de Contexto)" },
      { id: "RF-120", desc: "Campos SEO (meta title, description, focus keyword)" },
      { id: "RF-121", desc: "Sitemap XML, robots.txt, JSON-LD" },
      { id: "RF-122", desc: "Renderizacao via Server Components (zero JS)" },
      { id: "RF-123", desc: "Barra de progresso de leitura" },
      { id: "RF-124", desc: "Table of Contents automatico" },
      { id: "RF-125", desc: "Tempo estimado de leitura" },
    ],
  },
  {
    title: "Admin",
    icon: Settings,
    range: "RF-126 a RF-140",
    items: [
      { id: "RF-126", desc: "Dashboard com metricas agregadas" },
      { id: "RF-127", desc: "Gestao de usuarios (CRUD, roles, creditos)" },
      { id: "RF-128", desc: "Gestao de planos e precos" },
      { id: "RF-129", desc: "Analytics de uso (fluxos, cursos, IA)" },
      { id: "RF-130", desc: "Monitoramento de saude do sistema" },
      { id: "RF-131", desc: "QR Codes: CRUD, analytics, health check" },
      { id: "RF-132", desc: "Gestao de leads e captura" },
      { id: "RF-133", desc: "Sistema de notificacoes admin" },
      { id: "RF-134", desc: "Contratos e termos versionados" },
      { id: "RF-135", desc: "Configuracoes do site (logo, SEO, OG, scripts)" },
      { id: "RF-136", desc: "Criacao manual de usuario" },
      { id: "RF-137", desc: "Dashboard de custos de IA" },
      { id: "RF-138", desc: "Configuracao de provedores IA" },
      { id: "RF-139", desc: "Gestao de categorias de fluxo" },
      { id: "RF-140", desc: "Verificacao de permissao admin em todas as rotas" },
    ],
  },
  {
    title: "Pagamentos",
    icon: Wallet,
    range: "RF-141 a RF-152",
    items: [
      { id: "RF-141", desc: "Integracao Assas (PIX, boleto, cartao)" },
      { id: "RF-142", desc: "Checkout transparente (sem redirect)" },
      { id: "RF-143", desc: "Sales channels + products (arquitetura generica)" },
      { id: "RF-144", desc: "Webhooks Assas com HMAC SHA-256" },
      { id: "RF-145", desc: "Concessao automatica de acesso pos-pagamento" },
      { id: "RF-146", desc: "Pagina publica de planos" },
      { id: "RF-147", desc: "Checkout com endereco para livros fisicos" },
      { id: "RF-148", desc: "Rastreamento Meta Conversions API (server-side)" },
      { id: "RF-149", desc: "Produtos: cursos, livros, eventos, planos" },
      { id: "RF-150", desc: "Cupons de desconto no checkout" },
      { id: "RF-151", desc: "Historico de pagamentos por usuario" },
      { id: "RF-152", desc: "Webhook de reembolso" },
    ],
  },
  {
    title: "Notificacoes",
    icon: Radio,
    range: "RF-153 a RF-160",
    items: [
      { id: "RF-153", desc: "Fila de emails (email queue)" },
      { id: "RF-154", desc: "Notificacoes SMS via Twilio" },
      { id: "RF-155", desc: "Notificacoes WhatsApp via Evolution API" },
      { id: "RF-156", desc: "Webhooks de evento (configuravel por trigger)" },
      { id: "RF-157", desc: "Templates de notificacao" },
      { id: "RF-158", desc: "Log de entregas (delivery log)" },
      { id: "RF-159", desc: "Sequencias de funil (32 emails evergreen)" },
      { id: "RF-160", desc: "Agendamento de envio (scheduled_at)" },
    ],
  },
];

interface NfItem {
  id: string;
  desc: string;
  detail: string;
}

interface NfGroup {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  items: NfItem[];
}

const NF_GROUPS: NfGroup[] = [
  {
    title: "Performance",
    icon: Zap,
    items: [
      { id: "RNF-001", desc: "API p95 < 500ms", detail: "Excluindo chamadas IA" },
      { id: "RNF-002", desc: "IA < 15s (Claude) / < 30s (Ollama)", detail: "Timeout configuravel" },
      { id: "RNF-003", desc: "TTFB < 200ms", detail: "Vercel Edge Network" },
      { id: "RNF-004", desc: "100+ usuários concorrentes", detail: "Serverless auto-scale" },
      { id: "RNF-005", desc: "Cache de provedores IA", detail: "1 minuto TTL" },
      { id: "RNF-006", desc: "Cache de respostas IA", detail: "1 hora TTL" },
    ],
  },
  {
    title: "Segurança",
    icon: Shield,
    items: [
      { id: "RNF-007", desc: "RLS em todas as tabelas", detail: "PostgreSQL Row Level Security" },
      { id: "RNF-008", desc: "TLS em todas as conexões", detail: "HTTPS obrigatório" },
      { id: "RNF-009", desc: "Vault AES-256-GCM", detail: "Secrets criptografados no Supabase" },
      { id: "RNF-010", desc: "Anti-fraude SHA-256", detail: "Hash de telefone para trial" },
      { id: "RNF-011", desc: "Rate limit SMS", detail: "3 códigos/telefone/hora" },
      { id: "RNF-012", desc: "Max 5 tentativas por código", detail: "Bloqueio apos exceder" },
      { id: "RNF-013", desc: "Service_role apenas server-side", detail: "Nunca expor no client" },
      { id: "RNF-014", desc: "Verificação admin em todas as rotas", detail: "is_admin ou role check" },
      { id: "RNF-015", desc: "LGPD compliance", detail: "Export e exclusão de dados" },
      { id: "RNF-016", desc: "Audit log de ações admin", detail: "Rastreabilidade completa" },
    ],
  },
  {
    title: "Escalabilidade",
    icon: Server,
    items: [
      { id: "RNF-017", desc: "Vercel serverless auto-scale", detail: "Sem gerenciamento de servidores" },
      { id: "RNF-018", desc: "Supabase managed PostgreSQL", detail: "Backups automáticos" },
      { id: "RNF-019", desc: "Multi-LLM 4 provedores", detail: "Distribuição de carga" },
      { id: "RNF-020", desc: "Redis Upstash distribuido", detail: "Cache global, baixa latência" },
    ],
  },
  {
    title: "Disponibilidade",
    icon: Activity,
    items: [
      { id: "RNF-021", desc: "Uptime 99.5%", detail: "SLA mínimo aceitavel" },
      { id: "RNF-022", desc: "Zero erro visível ao aluno", detail: "Fallback em todas as camadas" },
      { id: "RNF-023", desc: "Mensagem amigavel em fallback", detail: "Nunca mostrar stack trace" },
    ],
  },
  {
    title: "SEO",
    icon: Search,
    items: [
      { id: "RNF-024", desc: "SSR para blog", detail: "Server Components" },
      { id: "RNF-025", desc: "Meta tags dinamicos", detail: "Open Graph por página" },
      { id: "RNF-026", desc: "Sitemap XML automático", detail: "Atualizado no build" },
      { id: "RNF-027", desc: "Robots.txt configurado", detail: "Crawlers permitidos" },
      { id: "RNF-028", desc: "JSON-LD estruturado", detail: "Schema.org para artigos" },
      { id: "RNF-029", desc: "Open Graph completo", detail: "Imagem, título, descrição" },
    ],
  },
];

const STACK_TABLE = [
  { layer: "Frontend", items: [{ tech: "Next.js", version: "15.5" }, { tech: "React", version: "19" }, { tech: "TypeScript", version: "5.9" }, { tech: "Tailwind CSS", version: "4" }] },
  { layer: "Backend / API", items: [{ tech: "Next.js API Routes", version: "15.5" }, { tech: "Supabase (PostgreSQL)", version: "managed" }] },
  { layer: "IA", items: [{ tech: "Claude Sonnet 4", version: "primary" }, { tech: "Groq Llama 3.1 8B", version: "fallback" }, { tech: "Gemini 2.0 Flash", version: "fallback" }, { tech: "Ollama Llama 3.2 3B", version: "local" }] },
  { layer: "Infraestrutura", items: [{ tech: "Vercel", version: "auto" }, { tech: "Hetzner VPS", version: "CX22" }, { tech: "Redis Upstash", version: "serverless" }, { tech: "pnpm", version: "9.x" }] },
  { layer: "Integrações", items: [{ tech: "Assas", version: "v3" }, { tech: "Twilio", version: "SMS" }, { tech: "Google TTS", version: "Chirp3-HD" }, { tech: "Tiptap", version: "v2" }, { tech: "Evolution API", version: "WhatsApp" }, { tech: "Meta Conversions API", version: "server-side" }] },
];

interface FlowStep {
  label: string;
}

interface UserFlow {
  title: string;
  steps: FlowStep[];
}

const USER_FLOWS: UserFlow[] = [
  {
    title: "Novo Usuario",
    steps: [
      { label: "Registro (email/senha ou Google)" },
      { label: "Confirmacao de email" },
      { label: "Login" },
      { label: "Trial SMS ou Plano" },
      { label: "Dashboard" },
    ],
  },
  {
    title: "Gerar Passaporte",
    steps: [
      { label: "Dashboard" },
      { label: "/app/passaporte" },
      { label: "Input (texto/PDF/entrevista)" },
      { label: "IA gera passaporte" },
      { label: "Passaporte pronto" },
      { label: "6 Segmentos" },
      { label: "Personas on-demand" },
    ],
  },
  {
    title: "Comprar Curso",
    steps: [
      { label: "Landing page do curso" },
      { label: "Checkout Assas" },
      { label: "Webhook confirma" },
      { label: "Acesso concedido" },
      { label: "Player Spotify-like" },
    ],
  },
  {
    title: "Admin",
    steps: [
      { label: "Login admin" },
      { label: "/admin" },
      { label: "Usuarios / Cursos / WhatsApp / Analytics" },
    ],
  },
  {
    title: "WhatsApp",
    steps: [
      { label: "Admin configura instancia" },
      { label: "Contatos sincronizam" },
      { label: "Auto-respostas / Broadcasts / Sequencias" },
    ],
  },
  {
    title: "Comprar Livro",
    steps: [
      { label: "/livro" },
      { label: "Checkout com endereco" },
      { label: "Pagamento Assas" },
      { label: "book_orders criado" },
      { label: "Email de entrega" },
    ],
  },
];

interface EntityField {
  name: string;
  type: string;
  note?: string;
}

interface Entity {
  table: string;
  fields: EntityField[];
}

const ENTITIES: Entity[] = [
  // MODULE: Auth & Profiles
  {
    table: "profiles",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "full_name", type: "TEXT" },
      { name: "is_admin", type: "BOOLEAN" },
      { name: "role", type: "TEXT" },
      { name: "phone", type: "TEXT" },
      { name: "phone_verified", type: "BOOLEAN" },
      { name: "status", type: "TEXT" },
    ],
  },
  {
    table: "admin_create_user_log",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "admin_id", type: "UUID", note: "FK" },
      { name: "target_email", type: "TEXT" },
      { name: "source", type: "TEXT" },
    ],
  },
  {
    table: "role_permissions",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "permission", type: "TEXT" },
      { name: "granted", type: "BOOLEAN" },
    ],
  },
  {
    table: "memberships",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "org_id", type: "UUID", note: "FK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "role", type: "TEXT" },
    ],
  },
  {
    table: "deleted_user_backups",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "original_user_id", type: "UUID" },
      { name: "user_email", type: "TEXT" },
      { name: "documents", type: "JSONB" },
      { name: "restored", type: "BOOLEAN" },
    ],
  },
  {
    table: "deletion_requests",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "status", type: "TEXT" },
      { name: "final_deletion_at", type: "TIMESTAMPTZ" },
      { name: "reason", type: "TEXT" },
    ],
  },

  // MODULE: Flows
  {
    table: "flows",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "name", type: "TEXT" },
      { name: "slug", type: "TEXT" },
      { name: "category", type: "TEXT" },
      { name: "active", type: "BOOLEAN" },
      { name: "credits_cost", type: "INT" },
      { name: "module_id", type: "UUID", note: "FK" },
    ],
  },
  {
    table: "flow_steps",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "flow_id", type: "UUID", note: "FK" },
      { name: "step_order", type: "INT" },
      { name: "title", type: "TEXT" },
      { name: "input_type", type: "TEXT" },
      { name: "prompt_template", type: "TEXT" },
    ],
  },
  {
    table: "flow_executions",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "flow_id", type: "UUID", note: "FK" },
      { name: "status", type: "TEXT" },
      { name: "current_step", type: "INT" },
      { name: "step_responses", type: "JSONB" },
    ],
  },
  {
    table: "flow_modules",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "name", type: "TEXT" },
      { name: "slug", type: "TEXT" },
      { name: "sort_order", type: "INT" },
      { name: "org_id", type: "UUID", note: "FK" },
    ],
  },
  {
    table: "flow_access",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "flow_id", type: "UUID", note: "FK" },
      { name: "granted_by", type: "TEXT" },
      { name: "expires_at", type: "TIMESTAMPTZ" },
    ],
  },
  {
    table: "flow_categories",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "name", type: "TEXT" },
      { name: "slug", type: "TEXT" },
      { name: "sort_order", type: "INT" },
    ],
  },
  {
    table: "flow_category_links",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "flow_id", type: "UUID", note: "FK" },
      { name: "category_id", type: "UUID", note: "FK" },
    ],
  },
  {
    table: "flow_step_versions",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "flow_step_id", type: "UUID", note: "FK" },
      { name: "version", type: "INT" },
      { name: "prompt_template", type: "TEXT" },
      { name: "is_active", type: "BOOLEAN" },
    ],
  },
  {
    table: "flow_analytics",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "flow_id", type: "UUID", note: "FK" },
      { name: "date", type: "TIMESTAMPTZ" },
      { name: "starts", type: "INT" },
      { name: "completions", type: "INT" },
      { name: "abandonments", type: "INT" },
    ],
  },

  // MODULE: Documents & Interview
  {
    table: "user_documents",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "flow_id", type: "UUID", note: "FK" },
      { name: "title", type: "TEXT" },
      { name: "content", type: "TEXT" },
      { name: "type", type: "TEXT" },
      { name: "version", type: "INT" },
    ],
  },
  {
    table: "interview_sessions",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "flow_id", type: "UUID", note: "FK" },
      { name: "status", type: "TEXT" },
      { name: "messages", type: "JSONB" },
      { name: "phase", type: "TEXT" },
    ],
  },
  {
    table: "passaporte_projects",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "slug", type: "TEXT" },
      { name: "expert_name", type: "TEXT" },
      { name: "status", type: "TEXT" },
      { name: "passaporte_data", type: "JSONB" },
    ],
  },

  // MODULE: Credits & Payments
  {
    table: "user_credits",
    fields: [
      { name: "user_id", type: "UUID", note: "PK" },
      { name: "balance", type: "INT" },
      { name: "total_purchased", type: "INT" },
      { name: "total_consumed", type: "INT" },
    ],
  },
  {
    table: "user_moedas",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "balance", type: "INT" },
      { name: "total_purchased", type: "INT" },
      { name: "total_consumed", type: "INT" },
    ],
  },
  {
    table: "credit_transactions",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "type", type: "TEXT" },
      { name: "amount", type: "INT" },
      { name: "balance_after", type: "INT" },
      { name: "description", type: "TEXT" },
    ],
  },
  {
    table: "moedas_transactions",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "type", type: "TEXT" },
      { name: "amount", type: "INT" },
      { name: "balance_after", type: "INT" },
    ],
  },
  {
    table: "moedas_admin_adjustments",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "admin_id", type: "UUID", note: "FK" },
      { name: "target_user_id", type: "UUID", note: "FK" },
      { name: "amount", type: "INT" },
      { name: "reason", type: "TEXT" },
    ],
  },
  {
    table: "credit_packs",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "name", type: "TEXT" },
      { name: "credits", type: "INT" },
      { name: "price_cents", type: "INT" },
      { name: "is_active", type: "BOOLEAN" },
    ],
  },
  {
    table: "coupons",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "code", type: "TEXT" },
      { name: "credits", type: "INT" },
      { name: "type", type: "TEXT" },
      { name: "active", type: "BOOLEAN" },
      { name: "max_redemptions", type: "INT" },
    ],
  },
  {
    table: "coupon_redemptions",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "coupon_id", type: "UUID", note: "FK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "credits_granted", type: "INT" },
    ],
  },
  {
    table: "user_signup_credits",
    fields: [
      { name: "user_id", type: "UUID", note: "PK" },
      { name: "credits_granted", type: "INT" },
      { name: "granted_at", type: "TIMESTAMPTZ" },
    ],
  },
  {
    table: "payment_records",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "type", type: "TEXT" },
      { name: "amount_cents", type: "INT" },
      { name: "status", type: "TEXT" },
    ],
  },
  {
    table: "payment_events",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "event_type", type: "TEXT" },
      { name: "payload", type: "JSONB" },
    ],
  },
  {
    table: "products",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "slug", type: "TEXT" },
      { name: "name", type: "TEXT" },
      { name: "type", type: "TEXT" },
      { name: "price_cents", type: "INT" },
      { name: "is_active", type: "BOOLEAN" },
    ],
  },
  {
    table: "product_versions",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "product_id", type: "UUID", note: "FK" },
      { name: "slug", type: "TEXT" },
      { name: "price_cents", type: "INT" },
      { name: "is_active", type: "BOOLEAN" },
    ],
  },
  {
    table: "sales_channels",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "slug", type: "TEXT" },
      { name: "name", type: "TEXT" },
      { name: "product_id", type: "UUID", note: "FK" },
      { name: "is_active", type: "BOOLEAN" },
    ],
  },
  {
    table: "plans",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "name", type: "TEXT" },
      { name: "slug", type: "TEXT" },
      { name: "price_cents", type: "INT" },
      { name: "credits_included", type: "INT" },
      { name: "active", type: "BOOLEAN" },
    ],
  },
  {
    table: "plan_flows",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "plan_id", type: "UUID", note: "FK" },
      { name: "flow_id", type: "UUID", note: "FK" },
    ],
  },
  {
    table: "subscriptions",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "plan", type: "TEXT" },
      { name: "status", type: "TEXT" },
      { name: "plan_id", type: "UUID", note: "FK" },
      { name: "amount_cents", type: "INT" },
    ],
  },

  // MODULE: Courses
  {
    table: "courses",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "slug", type: "TEXT" },
      { name: "title", type: "TEXT" },
      { name: "instructor", type: "TEXT" },
      { name: "total_lessons", type: "INT" },
      { name: "status", type: "TEXT" },
    ],
  },
  {
    table: "course_modules",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "course_id", type: "UUID", note: "FK" },
      { name: "slug", type: "TEXT" },
      { name: "title", type: "TEXT" },
      { name: "sort_order", type: "INT" },
    ],
  },
  {
    table: "course_lessons",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "module_id", type: "UUID", note: "FK" },
      { name: "slug", type: "TEXT" },
      { name: "title", type: "TEXT" },
      { name: "audio_url", type: "TEXT" },
      { name: "is_free", type: "BOOLEAN" },
    ],
  },
  {
    table: "course_progress",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "lesson_id", type: "UUID", note: "FK" },
      { name: "completed", type: "BOOLEAN" },
    ],
  },
  {
    table: "course_access",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "course_id", type: "UUID", note: "FK" },
      { name: "granted_by", type: "TEXT" },
    ],
  },
  {
    table: "course_completions",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "course_id", type: "UUID", note: "FK" },
      { name: "completed_at", type: "TIMESTAMPTZ" },
    ],
  },
  {
    table: "course_comments",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "lesson_id", type: "UUID", note: "FK" },
      { name: "content", type: "TEXT" },
      { name: "status", type: "TEXT" },
    ],
  },
  {
    table: "course_ratings",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "lesson_id", type: "UUID", note: "FK" },
      { name: "rating", type: "INT" },
    ],
  },
  {
    table: "course_org_access",
    fields: [
      { name: "org_id", type: "UUID", note: "PK" },
      { name: "course_id", type: "UUID", note: "FK" },
      { name: "can_sell", type: "BOOLEAN" },
      { name: "revenue_share_pct", type: "NUMERIC" },
    ],
  },
  {
    table: "course_notifications",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "course_id", type: "UUID", note: "FK" },
      { name: "type", type: "TEXT" },
    ],
  },

  // MODULE: Books
  {
    table: "book_projects",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "title", type: "TEXT" },
      { name: "slug", type: "TEXT" },
      { name: "author_name", type: "TEXT" },
      { name: "book_type", type: "TEXT" },
      { name: "current_stage", type: "TEXT" },
    ],
  },
  {
    table: "book_chapters",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "project_id", type: "UUID", note: "FK" },
      { name: "title", type: "TEXT" },
      { name: "chapter_number", type: "INT" },
      { name: "status", type: "TEXT" },
      { name: "word_count", type: "INT" },
    ],
  },
  {
    table: "book_reviews",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "chapter_id", type: "UUID", note: "FK" },
      { name: "review_type", type: "TEXT" },
      { name: "status", type: "TEXT" },
      { name: "findings", type: "JSONB" },
    ],
  },
  {
    table: "book_builds",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "project_id", type: "UUID", note: "FK" },
      { name: "build_type", type: "TEXT" },
      { name: "status", type: "TEXT" },
      { name: "output_url", type: "TEXT" },
    ],
  },
  {
    table: "book_checklists",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "chapter_id", type: "UUID", note: "FK" },
      { name: "items", type: "JSONB" },
      { name: "total_items", type: "INT" },
      { name: "checked_items", type: "INT" },
    ],
  },
  {
    table: "book_audio_chapters",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "build_id", type: "UUID", note: "FK" },
      { name: "chapter_id", type: "UUID", note: "FK" },
      { name: "status", type: "TEXT" },
      { name: "audio_url", type: "TEXT" },
    ],
  },
  {
    table: "book_orders",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "status", type: "TEXT" },
      { name: "amount_cents", type: "INT" },
      { name: "buyer_email", type: "TEXT" },
    ],
  },

  // MODULE: Blog & Content
  {
    table: "blog_posts",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "title", type: "TEXT" },
      { name: "slug", type: "TEXT" },
      { name: "status", type: "TEXT" },
      { name: "meta_title", type: "TEXT" },
      { name: "author_id", type: "UUID", note: "FK" },
    ],
  },
  {
    table: "blog_categories",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "name", type: "TEXT" },
      { name: "slug", type: "TEXT" },
      { name: "sort_order", type: "INT" },
    ],
  },
  {
    table: "blog_post_categories",
    fields: [
      { name: "post_id", type: "UUID", note: "FK" },
      { name: "category_id", type: "UUID", note: "FK" },
    ],
  },
  {
    table: "blog_ctas",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "key", type: "TEXT" },
      { name: "label", type: "TEXT" },
      { name: "button_url", type: "TEXT" },
      { name: "is_active", type: "BOOLEAN" },
    ],
  },
  {
    table: "web_stories",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "blog_post_id", type: "UUID", note: "FK" },
      { name: "slug", type: "TEXT" },
      { name: "title", type: "TEXT" },
      { name: "slides", type: "JSONB" },
      { name: "status", type: "TEXT" },
    ],
  },
  {
    table: "content_categories",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "slug", type: "TEXT" },
      { name: "name", type: "TEXT" },
      { name: "sort_order", type: "INT" },
    ],
  },
  {
    table: "system_pages",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "slug", type: "TEXT" },
      { name: "title", type: "TEXT" },
      { name: "content", type: "TEXT" },
    ],
  },
  {
    table: "docs",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "org_id", type: "UUID", note: "FK" },
      { name: "path", type: "TEXT" },
      { name: "title", type: "TEXT" },
      { name: "published", type: "BOOLEAN" },
    ],
  },

  // MODULE: WhatsApp
  {
    table: "whatsapp_instances",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "name", type: "TEXT" },
      { name: "provider", type: "TEXT" },
      { name: "phone_number", type: "TEXT" },
      { name: "connection_status", type: "TEXT" },
      { name: "is_active", type: "BOOLEAN" },
    ],
  },
  {
    table: "whatsapp_messages",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "instance_name", type: "TEXT" },
      { name: "direction", type: "TEXT" },
      { name: "phone_number", type: "TEXT" },
      { name: "body", type: "TEXT" },
      { name: "status", type: "TEXT" },
    ],
  },
  {
    table: "whatsapp_contacts",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "phone_e164", type: "TEXT" },
      { name: "push_name", type: "TEXT" },
      { name: "tags", type: "ARRAY" },
      { name: "conversation_status", type: "TEXT" },
      { name: "assigned_to", type: "UUID", note: "FK" },
    ],
  },
  {
    table: "whatsapp_contact_assignments",
    fields: [
      { name: "id", type: "INT", note: "PK" },
      { name: "contact_id", type: "UUID", note: "FK" },
      { name: "assigned_to", type: "UUID", note: "FK" },
      { name: "action", type: "TEXT" },
    ],
  },
  {
    table: "whatsapp_auto_responses",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "name", type: "TEXT" },
      { name: "trigger_type", type: "TEXT" },
      { name: "trigger_value", type: "TEXT" },
      { name: "response_text", type: "TEXT" },
      { name: "is_active", type: "BOOLEAN" },
    ],
  },
  {
    table: "whatsapp_auto_response_log",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "auto_response_id", type: "UUID", note: "FK" },
      { name: "phone_number", type: "TEXT" },
      { name: "response_sent_at", type: "TIMESTAMPTZ" },
    ],
  },
  {
    table: "whatsapp_broadcasts",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "name", type: "TEXT" },
      { name: "instance_name", type: "TEXT" },
      { name: "status", type: "TEXT" },
      { name: "total_recipients", type: "INT" },
    ],
  },
  {
    table: "whatsapp_broadcast_recipients",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "broadcast_id", type: "UUID", note: "FK" },
      { name: "phone_number", type: "TEXT" },
      { name: "status", type: "TEXT" },
    ],
  },
  {
    table: "whatsapp_sequences",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "name", type: "TEXT" },
      { name: "instance_name", type: "TEXT" },
      { name: "trigger_event", type: "TEXT" },
      { name: "is_active", type: "BOOLEAN" },
    ],
  },
  {
    table: "whatsapp_sequence_steps",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "sequence_id", type: "UUID", note: "FK" },
      { name: "step_order", type: "INT" },
      { name: "delay_minutes", type: "INT" },
      { name: "message_text", type: "TEXT" },
    ],
  },
  {
    table: "whatsapp_sequence_enrollments",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "sequence_id", type: "UUID", note: "FK" },
      { name: "phone_number", type: "TEXT" },
      { name: "current_step", type: "INT" },
      { name: "status", type: "TEXT" },
    ],
  },
  {
    table: "whatsapp_sequence_sends",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "enrollment_id", type: "UUID", note: "FK" },
      { name: "step_id", type: "UUID", note: "FK" },
      { name: "status", type: "TEXT" },
    ],
  },
  {
    table: "whatsapp_events",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "event_type", type: "TEXT" },
      { name: "phone_number", type: "TEXT" },
      { name: "status", type: "TEXT" },
      { name: "metadata", type: "JSONB" },
    ],
  },
  {
    table: "whatsapp_event_actions",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "name", type: "TEXT" },
      { name: "event_type", type: "TEXT" },
      { name: "action_type", type: "TEXT" },
      { name: "action_config", type: "JSONB" },
      { name: "is_active", type: "BOOLEAN" },
    ],
  },
  {
    table: "whatsapp_ai_agents",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "name", type: "TEXT" },
      { name: "system_prompt", type: "TEXT" },
      { name: "model", type: "TEXT" },
      { name: "is_active", type: "BOOLEAN" },
    ],
  },
  {
    table: "whatsapp_ai_agent_logs",
    fields: [
      { name: "id", type: "INT", note: "PK" },
      { name: "agent_id", type: "UUID", note: "FK" },
      { name: "contact_id", type: "UUID", note: "FK" },
      { name: "user_message", type: "TEXT" },
      { name: "agent_response", type: "TEXT" },
      { name: "success", type: "BOOLEAN" },
    ],
  },
  {
    table: "whatsapp_group_policies",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "group_jid", type: "TEXT" },
      { name: "allow_bots", type: "BOOLEAN" },
      { name: "allow_ai_agent", type: "BOOLEAN" },
      { name: "allow_broadcast", type: "BOOLEAN" },
    ],
  },
  {
    table: "whatsapp_settings",
    fields: [
      { name: "id", type: "INT", note: "PK" },
      { name: "respond_in_groups", type: "BOOLEAN" },
      { name: "webhook_token", type: "TEXT" },
    ],
  },
  {
    table: "whatsapp_conversation_view",
    fields: [
      { name: "id", type: "UUID" },
      { name: "phone_e164", type: "TEXT" },
      { name: "display_name", type: "TEXT" },
      { name: "conversation_status", type: "TEXT" },
      { name: "last_message_at", type: "TIMESTAMPTZ" },
    ],
  },

  // MODULE: Notifications
  {
    table: "notification_templates",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "event_type", type: "TEXT" },
      { name: "label", type: "TEXT" },
      { name: "email_enabled", type: "BOOLEAN" },
      { name: "whatsapp_enabled", type: "BOOLEAN" },
    ],
  },
  {
    table: "notification_log",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "event_type", type: "TEXT" },
      { name: "channel", type: "TEXT" },
      { name: "status", type: "TEXT" },
    ],
  },
  {
    table: "email_queue",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "email", type: "TEXT" },
      { name: "sequence", type: "TEXT" },
      { name: "step", type: "INT" },
      { name: "status", type: "TEXT" },
    ],
  },

  // MODULE: Leads & CRM
  {
    table: "leads",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "name", type: "TEXT" },
      { name: "email", type: "TEXT" },
      { name: "phone", type: "TEXT" },
      { name: "source", type: "TEXT" },
      { name: "status", type: "TEXT" },
    ],
  },
  {
    table: "ebook_leads",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "email", type: "TEXT" },
      { name: "name", type: "TEXT" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "is_new_user", type: "BOOLEAN" },
    ],
  },

  // MODULE: QR Codes
  {
    table: "qr_projects",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "name", type: "TEXT" },
      { name: "slug", type: "TEXT" },
      { name: "color", type: "TEXT" },
    ],
  },
  {
    table: "qr_codes",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "project_id", type: "UUID", note: "FK" },
      { name: "slug", type: "TEXT" },
      { name: "target_url", type: "TEXT" },
      { name: "scan_count", type: "INT" },
    ],
  },
  {
    table: "qr_scans",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "qr_code_id", type: "UUID", note: "FK" },
      { name: "scanned_at", type: "TIMESTAMPTZ" },
      { name: "ip_address", type: "TEXT" },
    ],
  },

  // MODULE: Multi-Tenant
  {
    table: "orgs",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "slug", type: "TEXT" },
      { name: "name", type: "TEXT" },
      { name: "owner_id", type: "UUID", note: "FK" },
      { name: "is_active", type: "BOOLEAN" },
    ],
  },
  {
    table: "org_domains",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "org_id", type: "UUID", note: "FK" },
      { name: "domain", type: "TEXT" },
      { name: "is_active", type: "BOOLEAN" },
    ],
  },

  // MODULE: Contracts & Legal
  {
    table: "contracts",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "title", type: "TEXT" },
      { name: "content", type: "TEXT" },
      { name: "status", type: "TEXT" },
      { name: "created_by", type: "UUID", note: "FK" },
    ],
  },
  {
    table: "contract_signatures",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "contract_id", type: "UUID", note: "FK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "status", type: "TEXT" },
      { name: "signed_at", type: "TIMESTAMPTZ" },
    ],
  },
  {
    table: "terms_versions",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "type", type: "TEXT" },
      { name: "version", type: "INT" },
      { name: "title", type: "TEXT" },
      { name: "is_active", type: "BOOLEAN" },
    ],
  },
  {
    table: "terms_acceptances",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "terms_version_id", type: "UUID", note: "FK" },
      { name: "accepted_at", type: "TIMESTAMPTZ" },
    ],
  },

  // MODULE: Workstation (ws_)
  {
    table: "ws_layers",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "name", type: "TEXT" },
      { name: "slug", type: "TEXT" },
      { name: "position", type: "INT" },
    ],
  },
  {
    table: "ws_tasks",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "layer_id", type: "UUID", note: "FK" },
      { name: "title", type: "TEXT" },
      { name: "bloom_level", type: "TEXT" },
      { name: "xp_reward", type: "INT" },
      { name: "position", type: "INT" },
    ],
  },
  {
    table: "ws_subtask_checks",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "task_id", type: "UUID", note: "FK" },
      { name: "subtask_index", type: "INT" },
      { name: "checked", type: "BOOLEAN" },
    ],
  },
  {
    table: "ws_evidences",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "task_id", type: "UUID", note: "FK" },
      { name: "type", type: "TEXT" },
      { name: "content", type: "TEXT" },
    ],
  },
  {
    table: "ws_member_profiles",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "nivel_tecnico", type: "TEXT" },
      { name: "motivacao", type: "TEXT" },
    ],
  },
  {
    table: "ws_achievements",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "achievement_key", type: "TEXT" },
      { name: "unlocked_at", type: "TIMESTAMPTZ" },
    ],
  },
  {
    table: "ws_streaks",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "current_streak", type: "INT" },
      { name: "longest_streak", type: "INT" },
      { name: "last_active_date", type: "TIMESTAMPTZ" },
    ],
  },
  {
    table: "ws_xp_log",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "action", type: "TEXT" },
      { name: "xp", type: "INT" },
      { name: "task_id", type: "UUID", note: "FK" },
    ],
  },
  {
    table: "ws_nps",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "trigger_point", type: "TEXT" },
      { name: "score", type: "INT" },
      { name: "comment", type: "TEXT" },
    ],
  },
  {
    table: "ws_card_analytics",
    fields: [
      { name: "task_id", type: "UUID", note: "PK" },
      { name: "total_started", type: "INT" },
      { name: "total_completed", type: "INT" },
      { name: "avg_time_hours", type: "NUMERIC" },
    ],
  },
  {
    table: "ws_support_threads",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "task_id", type: "UUID", note: "FK" },
      { name: "status", type: "TEXT" },
    ],
  },
  {
    table: "ws_support_messages",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "thread_id", type: "UUID", note: "FK" },
      { name: "sender_id", type: "UUID", note: "FK" },
      { name: "content", type: "TEXT" },
      { name: "is_ai", type: "BOOLEAN" },
    ],
  },
  {
    table: "ws_user_progress",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "task_id", type: "UUID", note: "FK" },
      { name: "status", type: "TEXT" },
      { name: "cs_score", type: "INT" },
    ],
  },

  // MODULE: Monitoring & Audit
  {
    table: "audit_logs",
    fields: [
      { name: "id", type: "INT", note: "PK" },
      { name: "actor_id", type: "UUID", note: "FK" },
      { name: "action", type: "TEXT" },
      { name: "resource_type", type: "TEXT" },
      { name: "resource_id", type: "TEXT" },
    ],
  },
  {
    table: "admin_audit_log",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "admin_id", type: "UUID", note: "FK" },
      { name: "action", type: "TEXT" },
      { name: "field_changed", type: "TEXT" },
      { name: "details", type: "JSONB" },
    ],
  },
  {
    table: "health_checks",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "run_id", type: "UUID", note: "FK" },
      { name: "check_name", type: "TEXT" },
      { name: "status", type: "TEXT" },
      { name: "severity", type: "TEXT" },
    ],
  },
  {
    table: "health_runs",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "org_id", type: "TEXT" },
      { name: "run_type", type: "TEXT" },
      { name: "total_checks", type: "INT" },
      { name: "worst_severity", type: "TEXT" },
    ],
  },
  {
    table: "health_alert_log",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "run_id", type: "UUID", note: "FK" },
      { name: "channel", type: "TEXT" },
      { name: "message", type: "TEXT" },
      { name: "success", type: "BOOLEAN" },
    ],
  },
  {
    table: "health_alert_rules",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "label", type: "TEXT" },
      { name: "category", type: "TEXT" },
      { name: "min_severity", type: "TEXT" },
      { name: "enabled", type: "BOOLEAN" },
    ],
  },
  {
    table: "worker_heartbeats",
    fields: [
      { name: "id", type: "TEXT", note: "PK" },
      { name: "worker_id", type: "TEXT" },
      { name: "status", type: "TEXT" },
      { name: "last_heartbeat_at", type: "TIMESTAMPTZ" },
    ],
  },
  {
    table: "gates_log",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "mensagem_id", type: "TEXT" },
      { name: "gate_numero", type: "INT" },
      { name: "resultado", type: "TEXT" },
      { name: "agent_id", type: "TEXT" },
    ],
  },
  {
    table: "product_changes_log",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "entity", type: "TEXT" },
      { name: "entity_id", type: "UUID" },
      { name: "action", type: "TEXT" },
      { name: "admin_id", type: "UUID", note: "FK" },
    ],
  },

  // MODULE: AI & Agents
  {
    table: "ai_providers",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "name", type: "TEXT" },
      { name: "display_name", type: "TEXT" },
      { name: "is_active", type: "BOOLEAN" },
      { name: "priority", type: "INT" },
    ],
  },
  {
    table: "ai_sessions",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "provider", type: "TEXT" },
      { name: "model", type: "TEXT" },
      { name: "input_tokens", type: "INT" },
      { name: "estimated_cost_usd", type: "NUMERIC" },
    ],
  },
  {
    table: "ai_usage_summary",
    fields: [
      { name: "total_sessions", type: "INT" },
      { name: "total_input_tokens", type: "NUMERIC" },
      { name: "total_cost_usd", type: "NUMERIC" },
      { name: "total_cost_brl", type: "NUMERIC" },
    ],
  },
  {
    table: "agent_logs",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "agent_id", type: "TEXT" },
      { name: "ferramenta", type: "TEXT" },
      { name: "sucesso", type: "BOOLEAN" },
      { name: "session_id", type: "TEXT" },
    ],
  },
  {
    table: "agent_states",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "agent_id", type: "TEXT" },
      { name: "session_id", type: "TEXT" },
      { name: "state", type: "JSONB" },
      { name: "nivel_ativo", type: "TEXT" },
    ],
  },
  {
    table: "memories",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "session_id", type: "TEXT" },
      { name: "content", type: "TEXT" },
      { name: "type", type: "TEXT" },
      { name: "domain", type: "TEXT" },
      { name: "importance", type: "INT" },
    ],
  },

  // MODULE: Community & Events
  {
    table: "community_events",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "title", type: "TEXT" },
      { name: "event_date", type: "TIMESTAMPTZ" },
      { name: "event_type", type: "TEXT" },
      { name: "is_published", type: "BOOLEAN" },
    ],
  },
  {
    table: "community_subscriptions",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "plan_type", type: "TEXT" },
      { name: "status", type: "TEXT" },
      { name: "expires_at", type: "TIMESTAMPTZ" },
    ],
  },
  {
    table: "event_registrations",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "event_id", type: "TEXT" },
      { name: "status", type: "TEXT" },
      { name: "amount_cents", type: "INT" },
    ],
  },
  {
    table: "mentoring_sessions",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "session_type", type: "TEXT" },
      { name: "status", type: "TEXT" },
      { name: "amount_cents", type: "INT" },
    ],
  },

  // MODULE: Trial
  {
    table: "phone_verifications",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "phone", type: "TEXT" },
      { name: "phone_hash", type: "TEXT" },
      { name: "code", type: "TEXT" },
      { name: "verified", type: "BOOLEAN" },
      { name: "user_id", type: "UUID", note: "FK" },
    ],
  },
  {
    table: "used_trial_phones",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "phone_hash", type: "TEXT" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "used_at", type: "TIMESTAMPTZ" },
    ],
  },
  {
    table: "used_trial_emails",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "email_hash", type: "TEXT" },
      { name: "email", type: "TEXT" },
      { name: "user_id", type: "UUID", note: "FK" },
    ],
  },
  {
    table: "user_trials",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "user_id", type: "UUID", note: "FK" },
      { name: "phone_hash", type: "TEXT" },
      { name: "status", type: "TEXT" },
      { name: "expires_at", type: "TIMESTAMPTZ" },
    ],
  },

  // MODULE: Other
  {
    table: "vault",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "label", type: "TEXT" },
      { name: "category", type: "TEXT" },
      { name: "encrypted_data", type: "TEXT" },
    ],
  },
  {
    table: "site_settings",
    fields: [
      { name: "key", type: "TEXT", note: "PK" },
      { name: "value", type: "TEXT" },
      { name: "updated_at", type: "TIMESTAMPTZ" },
    ],
  },
  {
    table: "support_tickets",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "ticket_number", type: "INT" },
      { name: "email", type: "TEXT" },
      { name: "message", type: "TEXT" },
      { name: "status", type: "TEXT" },
    ],
  },
  {
    table: "tasks",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "title", type: "TEXT" },
      { name: "status", type: "TEXT" },
      { name: "priority", type: "INT" },
      { name: "category", type: "TEXT" },
    ],
  },
  {
    table: "hotmart_members",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "email", type: "TEXT" },
      { name: "transaction_id", type: "TEXT" },
      { name: "status", type: "TEXT" },
    ],
  },
  {
    table: "external_costs",
    fields: [
      { name: "id", type: "UUID", note: "PK" },
      { name: "service", type: "TEXT" },
      { name: "period", type: "TEXT" },
      { name: "amount_usd", type: "NUMERIC" },
      { name: "source", type: "TEXT" },
    ],
  },
  {
    table: "cost_summary_full",
    fields: [
      { name: "period", type: "TEXT" },
      { name: "ext_usd", type: "NUMERIC" },
      { name: "ext_brl", type: "NUMERIC" },
      { name: "breakdown", type: "TEXT" },
    ],
  },
];

interface RlsRow {
  table: string;
  select: string;
  insert: string;
  update: string;
}

const RLS_TABLE: RlsRow[] = [
  // Auth & Profiles
  { table: "profiles", select: "Proprio", insert: "Service", update: "Proprio" },
  { table: "admin_create_user_log", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "role_permissions", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "memberships", select: "Proprio", insert: "Service", update: "Service" },
  { table: "deleted_user_backups", select: "Admin", insert: "Service", update: "Admin" },
  { table: "deletion_requests", select: "Proprio", insert: "Proprio", update: "Service" },
  // Flows
  { table: "flows", select: "Público", insert: "Admin", update: "Admin" },
  { table: "flow_steps", select: "Público", insert: "Admin", update: "Admin" },
  { table: "flow_executions", select: "Proprio", insert: "Proprio", update: "Proprio" },
  { table: "flow_modules", select: "Público", insert: "Admin", update: "Admin" },
  { table: "flow_access", select: "Proprio", insert: "Service", update: "Service" },
  { table: "flow_categories", select: "Público", insert: "Admin", update: "Admin" },
  { table: "flow_category_links", select: "Público", insert: "Admin", update: "Admin" },
  { table: "flow_step_versions", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "flow_analytics", select: "Admin", insert: "Service", update: "Service" },
  // Documents & Interview
  { table: "user_documents", select: "Proprio", insert: "Service", update: "Proprio" },
  { table: "interview_sessions", select: "Proprio", insert: "Service", update: "Service" },
  { table: "passaporte_projects", select: "Proprio", insert: "Service", update: "Proprio" },
  // Credits & Payments
  { table: "user_credits", select: "Proprio", insert: "Service", update: "Service" },
  { table: "user_moedas", select: "Proprio", insert: "Service", update: "Service" },
  { table: "credit_transactions", select: "Proprio", insert: "Service", update: "Service" },
  { table: "moedas_transactions", select: "Proprio", insert: "Service", update: "Service" },
  { table: "moedas_admin_adjustments", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "credit_packs", select: "Público", insert: "Admin", update: "Admin" },
  { table: "coupons", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "coupon_redemptions", select: "Proprio", insert: "Service", update: "Service" },
  { table: "user_signup_credits", select: "Proprio", insert: "Service", update: "Service" },
  { table: "payment_records", select: "Proprio", insert: "Service", update: "Service" },
  { table: "payment_events", select: "Admin", insert: "Service", update: "Service" },
  { table: "products", select: "Público", insert: "Admin", update: "Admin" },
  { table: "product_versions", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "sales_channels", select: "Público", insert: "Admin", update: "Admin" },
  { table: "plans", select: "Público", insert: "Admin", update: "Admin" },
  { table: "plan_flows", select: "Público", insert: "Admin", update: "Admin" },
  { table: "subscriptions", select: "Proprio", insert: "Service", update: "Service" },
  // Courses
  { table: "courses", select: "Público", insert: "Admin", update: "Admin" },
  { table: "course_modules", select: "Público", insert: "Admin", update: "Admin" },
  { table: "course_lessons", select: "Público", insert: "Admin", update: "Admin" },
  { table: "course_progress", select: "Proprio", insert: "Service", update: "Proprio" },
  { table: "course_access", select: "Proprio", insert: "Service", update: "Service" },
  { table: "course_completions", select: "Proprio", insert: "Service", update: "Service" },
  { table: "course_comments", select: "Proprio", insert: "Proprio", update: "Proprio" },
  { table: "course_ratings", select: "Proprio", insert: "Proprio", update: "Proprio" },
  { table: "course_org_access", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "course_notifications", select: "Proprio", insert: "Service", update: "Service" },
  // Books
  { table: "book_projects", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "book_chapters", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "book_reviews", select: "Admin", insert: "Service", update: "Admin" },
  { table: "book_builds", select: "Admin", insert: "Service", update: "Service" },
  { table: "book_checklists", select: "Admin", insert: "Service", update: "Admin" },
  { table: "book_audio_chapters", select: "Admin", insert: "Service", update: "Service" },
  { table: "book_orders", select: "Proprio", insert: "Service", update: "Service" },
  // Blog & Content
  { table: "blog_posts", select: "Público", insert: "Admin", update: "Admin" },
  { table: "blog_categories", select: "Público", insert: "Admin", update: "Admin" },
  { table: "blog_post_categories", select: "Público", insert: "Admin", update: "Admin" },
  { table: "blog_ctas", select: "Público", insert: "Admin", update: "Admin" },
  { table: "web_stories", select: "Público", insert: "Admin", update: "Admin" },
  { table: "content_categories", select: "Público", insert: "Admin", update: "Admin" },
  { table: "system_pages", select: "Público", insert: "Admin", update: "Admin" },
  { table: "docs", select: "Público", insert: "Admin", update: "Admin" },
  // WhatsApp
  { table: "whatsapp_instances", select: "Admin", insert: "Service", update: "Service" },
  { table: "whatsapp_messages", select: "Admin", insert: "Service", update: "Service" },
  { table: "whatsapp_contacts", select: "Admin", insert: "Service", update: "Service" },
  { table: "whatsapp_contact_assignments", select: "Admin", insert: "Service", update: "Service" },
  { table: "whatsapp_auto_responses", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "whatsapp_auto_response_log", select: "Admin", insert: "Service", update: "Service" },
  { table: "whatsapp_broadcasts", select: "Admin", insert: "Admin", update: "Service" },
  { table: "whatsapp_broadcast_recipients", select: "Admin", insert: "Service", update: "Service" },
  { table: "whatsapp_sequences", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "whatsapp_sequence_steps", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "whatsapp_sequence_enrollments", select: "Admin", insert: "Service", update: "Service" },
  { table: "whatsapp_sequence_sends", select: "Admin", insert: "Service", update: "Service" },
  { table: "whatsapp_events", select: "Admin", insert: "Service", update: "Service" },
  { table: "whatsapp_event_actions", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "whatsapp_ai_agents", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "whatsapp_ai_agent_logs", select: "Admin", insert: "Service", update: "Service" },
  { table: "whatsapp_group_policies", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "whatsapp_settings", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "whatsapp_conversation_view", select: "Admin", insert: "Service", update: "Service" },
  // Notifications
  { table: "notification_templates", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "notification_log", select: "Admin", insert: "Service", update: "Service" },
  { table: "email_queue", select: "Admin", insert: "Service", update: "Service" },
  // Leads & CRM
  { table: "leads", select: "Admin", insert: "Service", update: "Admin" },
  { table: "ebook_leads", select: "Admin", insert: "Service", update: "Service" },
  // QR Codes
  { table: "qr_projects", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "qr_codes", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "qr_scans", select: "Admin", insert: "Service", update: "Service" },
  // Multi-Tenant
  { table: "orgs", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "org_domains", select: "Admin", insert: "Admin", update: "Admin" },
  // Contracts & Legal
  { table: "contracts", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "contract_signatures", select: "Proprio", insert: "Service", update: "Service" },
  { table: "terms_versions", select: "Público", insert: "Admin", update: "Admin" },
  { table: "terms_acceptances", select: "Proprio", insert: "Proprio", update: "Service" },
  // Workstation (ws_)
  { table: "ws_layers", select: "Público", insert: "Admin", update: "Admin" },
  { table: "ws_tasks", select: "Público", insert: "Admin", update: "Admin" },
  { table: "ws_subtask_checks", select: "Proprio", insert: "Service", update: "Proprio" },
  { table: "ws_evidences", select: "Proprio", insert: "Service", update: "Proprio" },
  { table: "ws_member_profiles", select: "Proprio", insert: "Service", update: "Proprio" },
  { table: "ws_achievements", select: "Proprio", insert: "Service", update: "Service" },
  { table: "ws_streaks", select: "Proprio", insert: "Service", update: "Proprio" },
  { table: "ws_xp_log", select: "Proprio", insert: "Service", update: "Service" },
  { table: "ws_nps", select: "Proprio", insert: "Proprio", update: "Service" },
  { table: "ws_card_analytics", select: "Admin", insert: "Service", update: "Service" },
  { table: "ws_support_threads", select: "Proprio", insert: "Service", update: "Proprio" },
  { table: "ws_support_messages", select: "Proprio", insert: "Service", update: "Service" },
  { table: "ws_user_progress", select: "Proprio", insert: "Service", update: "Proprio" },
  // Monitoring & Audit
  { table: "audit_logs", select: "Admin", insert: "Service", update: "Service" },
  { table: "admin_audit_log", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "health_checks", select: "Admin", insert: "Service", update: "Service" },
  { table: "health_runs", select: "Admin", insert: "Service", update: "Service" },
  { table: "health_alert_log", select: "Admin", insert: "Service", update: "Service" },
  { table: "health_alert_rules", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "worker_heartbeats", select: "Admin", insert: "Service", update: "Service" },
  { table: "gates_log", select: "Admin", insert: "Service", update: "Service" },
  { table: "product_changes_log", select: "Admin", insert: "Service", update: "Service" },
  // AI & Agents
  { table: "ai_providers", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "ai_sessions", select: "Admin", insert: "Service", update: "Service" },
  { table: "ai_usage_summary", select: "Admin", insert: "Service", update: "Service" },
  { table: "agent_logs", select: "Admin", insert: "Service", update: "Service" },
  { table: "agent_states", select: "Admin", insert: "Service", update: "Service" },
  { table: "memories", select: "Admin", insert: "Service", update: "Service" },
  // Community & Events
  { table: "community_events", select: "Público", insert: "Admin", update: "Admin" },
  { table: "community_subscriptions", select: "Proprio", insert: "Service", update: "Service" },
  { table: "event_registrations", select: "Proprio", insert: "Service", update: "Service" },
  { table: "mentoring_sessions", select: "Proprio", insert: "Service", update: "Service" },
  // Trial
  { table: "phone_verifications", select: "Service", insert: "Service", update: "Service" },
  { table: "used_trial_phones", select: "Service", insert: "Service", update: "Service" },
  { table: "used_trial_emails", select: "Service", insert: "Service", update: "Service" },
  { table: "user_trials", select: "Proprio", insert: "Service", update: "Service" },
  // Other
  { table: "vault", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "site_settings", select: "Público", insert: "Admin", update: "Admin" },
  { table: "support_tickets", select: "Admin", insert: "Público", update: "Admin" },
  { table: "tasks", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "hotmart_members", select: "Admin", insert: "Service", update: "Service" },
  { table: "external_costs", select: "Admin", insert: "Admin", update: "Admin" },
  { table: "cost_summary_full", select: "Admin", insert: "Service", update: "Service" },
];

interface ApiRoute {
  method: "GET" | "POST" | "PUT" | "DELETE";
  path: string;
}

interface ApiGroup {
  title: string;
  routes: ApiRoute[];
}

const API_GROUPS: ApiGroup[] = [
  {
    title: "Fluxos",
    routes: [
      { method: "GET", path: "/api/admin/fluxos" },
      { method: "POST", path: "/api/admin/fluxos" },
      { method: "PUT", path: "/api/admin/fluxos/[id]" },
      { method: "DELETE", path: "/api/admin/fluxos/[id]" },
      { method: "POST", path: "/api/flow/execute" },
      { method: "POST", path: "/api/flow/suggest" },
      { method: "POST", path: "/api/flow/complete" },
      { method: "POST", path: "/api/flow/import" },
    ],
  },
  {
    title: "Entrevista",
    routes: [
      { method: "POST", path: "/api/interview/start" },
      { method: "POST", path: "/api/interview/message" },
      { method: "POST", path: "/api/interview/audio" },
      { method: "POST", path: "/api/interview/upload" },
      { method: "POST", path: "/api/interview/abandon" },
      { method: "GET", path: "/api/interview/session" },
      { method: "GET", path: "/api/interview/list" },
    ],
  },
  {
    title: "Documentos",
    routes: [
      { method: "GET", path: "/api/documents/[id]" },
      { method: "PUT", path: "/api/documents/[id]" },
      { method: "POST", path: "/api/documents/[id]/share" },
      { method: "DELETE", path: "/api/documents/[id]/share" },
      { method: "GET", path: "/api/documents/shared/[token]" },
    ],
  },
  {
    title: "Créditos",
    routes: [
      { method: "GET", path: "/api/credits/balance" },
      { method: "GET", path: "/api/credits/history" },
      { method: "POST", path: "/api/admin/credits/adjust" },
    ],
  },
  {
    title: "Blog",
    routes: [
      { method: "GET", path: "/api/admin/blog" },
      { method: "POST", path: "/api/admin/blog" },
      { method: "PUT", path: "/api/admin/blog/[slug]" },
      { method: "DELETE", path: "/api/admin/blog/[slug]" },
      { method: "GET", path: "/api/admin/blog/categorias" },
      { method: "POST", path: "/api/admin/blog/categorias" },
      { method: "POST", path: "/api/admin/blog/upload" },
    ],
  },
  {
    title: "QR Codes",
    routes: [
      { method: "GET", path: "/qr/[slug]" },
      { method: "GET", path: "/api/admin/qr/projects" },
      { method: "POST", path: "/api/admin/qr/projects" },
      { method: "GET", path: "/api/admin/qr/codes" },
      { method: "POST", path: "/api/admin/qr/codes" },
      { method: "GET", path: "/api/admin/qr/analytics" },
      { method: "POST", path: "/api/admin/qr/health-check" },
      { method: "POST", path: "/api/admin/qr/import" },
      { method: "GET", path: "/api/admin/qr/export" },
    ],
  },
  {
    title: "Auth / Trial",
    routes: [
      { method: "POST", path: "/api/auth/send-code" },
      { method: "POST", path: "/api/auth/verify-code" },
    ],
  },
  {
    title: "Planos / Pagamentos",
    routes: [
      { method: "GET", path: "/api/admin/planos" },
      { method: "POST", path: "/api/admin/planos" },
      { method: "PUT", path: "/api/admin/planos/[id]" },
      { method: "POST", path: "/api/checkout" },
      { method: "POST", path: "/api/webhooks/assas" },
    ],
  },
  {
    title: "IA",
    routes: [
      { method: "GET", path: "/api/admin/ia/providers" },
      { method: "PUT", path: "/api/admin/ia/providers" },
      { method: "GET", path: "/api/admin/ia/health" },
    ],
  },
  {
    title: "Termos",
    routes: [
      { method: "GET", path: "/api/terms/current" },
      { method: "POST", path: "/api/terms/accept" },
      { method: "GET", path: "/api/terms/check" },
    ],
  },
];

interface Integration {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  features: string[];
}

const INTEGRATIONS: Integration[] = [
  {
    title: "AI Router (Multi-LLM)",
    icon: Brain,
    features: [
      "Claude Sonnet 4 - primario (sugestoes, entrevista, passaporte)",
      "Groq Llama 3.1 8B - validacao, fallback",
      "Gemini 2.0 Flash - fallback secundario",
      "Ollama Llama 3.2 3B - local no VPS",
      "Whisper Large v3 - transcricao de audio",
      "Cache em memoria (1h TTL)",
      "Log completo em ai_sessions",
    ],
  },
  {
    title: "Assas (Pagamentos)",
    icon: Wallet,
    features: [
      "API REST em producao",
      "PIX, boleto e cartao de credito",
      "Checkout transparente (sem redirect)",
      "Webhooks com HMAC SHA-256",
      "Sales channels + products (arquitetura generica)",
    ],
  },
  {
    title: "Twilio (SMS)",
    icon: Smartphone,
    features: [
      "Codigo de 6 digitos para trial",
      "Normalizacao E.164 para Brasil (+55)",
      "Rate limiting: 3 codigos/hora",
      "Max 5 tentativas por codigo",
      "Expiracao em 10 minutos",
    ],
  },
  {
    title: "Evolution API (WhatsApp)",
    icon: MessageSquare,
    features: [
      "Multi-instancia (Baileys + Cloud API)",
      "Inbox unificado com contatos",
      "Broadcasts, auto-respostas, sequencias",
      "AI Agents com system prompt",
      "Templates HSM (Meta aprovacao)",
      "Webhooks com HMAC e apikey auth",
    ],
  },
  {
    title: "Meta Conversions API",
    icon: TrendingUp,
    features: [
      "Rastreamento server-side de eventos",
      "Purchase, Lead, PageView",
      "Integracao com checkout Assas",
      "Deduplicacao com pixel browser",
    ],
  },
  {
    title: "Redis Upstash",
    icon: HardDrive,
    features: [
      "Cache de sessoes",
      "Rate limiting distribuido",
      "Dados temporarios com TTL",
    ],
  },
  {
    title: "Supabase",
    icon: Database,
    features: [
      "PostgreSQL com RLS em todas as tabelas",
      "Auth: Google OAuth + email/senha",
      "Storage: blog-images, interview-files, qr-images, whatsapp-media",
      "Vault: AES-256-GCM para secrets",
    ],
  },
  {
    title: "Google TTS",
    icon: Mic,
    features: [
      "Chirp3-HD - voz masculina premium",
      "Audio para cursos APE, Vibecoding PRO, Pre-Vibecoding",
      "Audio chapters para Maquina de Livros",
      "Armazenado no Supabase Storage",
    ],
  },
];

const DEPLOY_ENV_VARS = `# Supabase
NEXT_PUBLIC_SUPABASE_URL
NEXT_PUBLIC_SUPABASE_ANON_KEY
SUPABASE_SERVICE_ROLE_KEY
SUPABASE_DB_PASSWORD
SUPABASE_PROJECT_REF
SUPABASE_ACCESS_TOKEN

# IA
ANTHROPIC_API_KEY
GROQ_API_KEY
GEMINI_API_KEY
OLLAMA_API_URL

# Pagamentos
ASSAS_API_KEY
ASSAS_API_URL

# SMS
TWILIO_ACCOUNT_SID
TWILIO_AUTH_TOKEN
TWILIO_PHONE_NUMBER

# WhatsApp (Evolution API)
EVOLUTION_API_URL
EVOLUTION_API_KEY

# Meta
META_PIXEL_ID
META_CONVERSIONS_API_TOKEN

# Cache
REDIS_URL
REDIS_TOKEN

# Seguranca
VAULT_KEY
HMAC_SECRET

# App
NEXT_PUBLIC_APP_URL
VERCEL_TOKEN
HETZNER_HOST
HETZNER_USER`;

const DEPLOY_STEPS = [
  "Rodar pnpm build localmente (verificar compilacao)",
  "Commitar com mensagem descritiva (prefixo convencional)",
  "Push para main no GitHub (ebuz-ia/ebuz-docs)",
  "Vercel detecta push e inicia build automático",
  "Preview deploy disponível para verificação",
  "Promote para produção (automático para main)",
  "Verificar /api/health no dominio de produção",
];

const MIGRATIONS = [
  "001_multi_tenant.sql",
  "002_docs.sql",
  "003_memories.sql",
  "004_agents.sql",
  "005_vault.sql",
  "006_sessions.sql",
  "010_subscriptions.sql",
  "012_plans_flows.sql",
  "018_flow_engine_core.sql",
  "019_document_library.sql",
  "020_credits_system.sql",
  "025_coupons_signup.sql",
  "031_interview_sessions.sql",
];

interface TestItem {
  name: string;
  priority: "alta" | "media" | "baixa";
}

interface TestGroup {
  title: string;
  items: TestItem[];
}

const TEST_GROUPS: TestGroup[] = [
  {
    title: "Testes Unitarios",
    items: [
      { name: "normalizePhone", priority: "alta" },
      { name: "hashPhone", priority: "alta" },
      { name: "generateCode", priority: "alta" },
      { name: "maskPhone", priority: "baixa" },
      { name: "hasEnoughCredits", priority: "alta" },
      { name: "consumeCredits", priority: "alta" },
      { name: "quickValidate", priority: "media" },
      { name: "validateInput", priority: "media" },
      { name: "replaceVariables", priority: "media" },
      { name: "generateDocument", priority: "media" },
      { name: "normalizacao de cupom", priority: "media" },
      { name: "determinePhase", priority: "media" },
      { name: "parseJhowResponse", priority: "media" },
      { name: "exportAsHTML / exportAsMarkdown", priority: "baixa" },
    ],
  },
  {
    title: "Testes de Integração",
    items: [
      { name: "Fluxo completo (início -> documento)", priority: "alta" },
      { name: "Trial completo (SMS -> ativação)", priority: "alta" },
      { name: "Créditos (concessão -> consumo -> saldo)", priority: "alta" },
      { name: "Cupom (resgate -> créditos)", priority: "media" },
      { name: "Blog (criar -> publicar -> listar)", priority: "media" },
      { name: "Entrevista (início -> fases -> documento)", priority: "alta" },
      { name: "AI Router fallback (Claude off -> Groq)", priority: "alta" },
      { name: "AI Router cache hit", priority: "media" },
    ],
  },
  {
    title: "Testes E2E",
    items: [
      { name: "Registro e login completo", priority: "alta" },
      { name: "Trial SMS end-to-end", priority: "alta" },
      { name: "Fluxo educacional completo", priority: "alta" },
      { name: "Biblioteca de documentos", priority: "media" },
      { name: "Admin: gestão de fluxos", priority: "media" },
      { name: "Admin: gestão de blog", priority: "media" },
      { name: "Pagamento e ativação de plano", priority: "alta" },
      { name: "Entrevista estruturada completa", priority: "alta" },
    ],
  },
];

const CRITICAL_SCENARIOS = [
  "Debito duplo de créditos (race condition na execução simultanea)",
  "Race condition em resgate de cupons (two-phase commit)",
  "Trial anti-fraude (mesmo telefone em contas diferentes)",
  "Fallback de IA (Claude fora do ar, Groq assume sem erro visível)",
  "RLS bypass (tentativa de acessar dados de outro usuário)",
  "Admin access (rota admin acessada por usuário normal)",
  "Versionamento de documento (criar nova versão sem perder anterior)",
  "Expiração de trial (créditos bloqueados apos 24h)",
];

interface Adr {
  id: string;
  title: string;
  status: string;
  context: string;
  decision: string;
  rejected: string;
}

const ADRS: Adr[] = [
  {
    id: "ADR-001",
    title: "Next.js vs Vite",
    status: "Aceito",
    context: "Precisavamos de SSR para SEO do blog, API Routes integradas e deploy simplificado.",
    decision: "Next.js 15.5 com App Router. Server Components para blog, API Routes para backend, Vercel para auto-deploy.",
    rejected: "Vite + Express separado. Exigiria dois deploys, sem SSR nativo, mais complexidade operacional.",
  },
  {
    id: "ADR-002",
    title: "Supabase vs Firebase",
    status: "Aceito",
    context: "O modelo de dados exige relações complexas (fluxos, steps, execuções, créditos, documentos).",
    decision: "Supabase com PostgreSQL real. SQL completo (JOINs, CTEs, triggers), RLS nativo, Vault para secrets.",
    rejected: "Firebase Firestore. NoSQL não atende relações complexas. Sem RLS nativo. Vendor lock-in Google.",
  },
  {
    id: "ADR-003",
    title: "Multi-LLM com Fallback",
    status: "Aceito",
    context: "Depender de um único provedor de IA e risco de downtime e custo imprevisível.",
    decision: "AI Router com 4 provedores (Claude -> Groq -> Gemini -> Ollama -> Cache). Zero downtime visível, otimização de custo.",
    rejected: "Único provedor (apenas Claude). Risco de downtime total e custo elevado sem alternativa.",
  },
  {
    id: "ADR-004",
    title: "Créditos vs Unlimited",
    status: "Aceito",
    context: "Chamadas de IA tem custo real por token. Plano unlimited incentiva abuso.",
    decision: "Sistema de créditos com moedas. Custo previsível, monetização por valor entregue, flexibilidade de planos.",
    rejected: "Plano unlimited. Risco de abuso por poucos usuários gerando custo insustentável.",
  },
  {
    id: "ADR-005",
    title: "Trial via SMS vs Email",
    status: "Aceito",
    context: "Trial por email permite criar infinitas contas com emails descartaveis.",
    decision: "Trial via SMS com verificação. Anti-fraude robusto (1 chip = 1 trial), hash SHA-256, urgencia de 24h.",
    rejected: "Trial via email. Fácil criar múltiplas contas, sem custo de fraude para o abusador.",
  },
  {
    id: "ADR-006",
    title: "Rate Limiting de Alertas",
    status: "Aceito",
    context: "Sistema de monitoramento gerava excesso de falsos positivos (processos normais do sistema).",
    decision: "Retry 3x com intervalo de 10s, filtrar processos normais conhecidos, cooldown aumentado. Reducao de 80%+ em falsos positivos.",
    rejected: "Alertas sem filtro. Excesso de notificações leva a alert fatigue e alertas reais ignorados.",
  },
];

// ---------------------------------------------------------------------------
// HELPERS
// ---------------------------------------------------------------------------

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

function MethodBadge({ method }: { method: string }) {
  const colors: Record<string, string> = {
    GET: "bg-emerald-100 text-emerald-700",
    POST: "bg-blue-100 text-blue-700",
    PUT: "bg-amber-100 text-amber-700",
    DELETE: "bg-red-100 text-red-700",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-mono font-semibold ${colors[method] || "bg-gray-100 text-gray-700"}`}>
      {method}
    </span>
  );
}

function RlsBadge({ level }: { level: string }) {
  const colors: Record<string, string> = {
    Proprio: "bg-violet-100 text-violet-700",
    Admin: "bg-amber-100 text-amber-700",
    Publico: "bg-emerald-100 text-emerald-700",
    Service: "bg-gray-100 text-gray-600",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${colors[level] || "bg-gray-100"}`}>
      {level}
    </span>
  );
}

function PriorityBadge({ priority }: { priority: string }) {
  const colors: Record<string, string> = {
    alta: "bg-red-100 text-red-700",
    media: "bg-amber-100 text-amber-700",
    baixa: "bg-emerald-100 text-emerald-700",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold capitalize ${colors[priority] || "bg-gray-100"}`}>
      {priority}
    </span>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-violet-600 uppercase tracking-wider text-xs font-semibold mb-3">
      {children}
    </p>
  );
}

// ---------------------------------------------------------------------------
// COMPONENT
// ---------------------------------------------------------------------------

export default function SDDPage() {
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const [expandedModules, setExpandedModules] = useState<Set<number>>(new Set());
  const [expandedAdrs, setExpandedAdrs] = useState<Set<number>>(new Set());
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const toggleModule = (idx: number) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const toggleAdr = (idx: number) => {
    setExpandedAdrs((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleScroll = useCallback(() => {
    const offset = 120;
    let current = SECTIONS[0].id;
    for (const s of SECTIONS) {
      const el = sectionRefs.current[s.id];
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= offset) current = s.id;
      }
    }
    setActiveSection(current);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const scrollTo = (id: string) => {
    const el = sectionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const totalReqs = FUNCTIONAL_MODULES.reduce((a, m) => a + m.items.length, 0);

  return (
    <div className="min-h-screen bg-[#faf7f5]">
      {/* ---- HEADER ---- */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              href="/hub"
              className="text-gray-500 hover:text-violet-600 transition flex items-center gap-1 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Voltar ao Hub</span>
            </Link>
            <div className="h-5 w-px bg-gray-300 hidden sm:block" />
            <h1 className="text-lg font-bold text-gray-900">
              eBuz{" "}
              <span className="ml-2 inline-block text-xs font-semibold bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">
                v1.0 - Produção
              </span>
            </h1>
          </div>
          <span className="text-xs text-gray-400 hidden md:block">
            Última atualização: Marco 2026 | app.ebuz.com.br
          </span>
        </div>
      </header>

      {/* ---- MOBILE TABS ---- */}
      <nav className="lg:hidden sticky top-[57px] z-30 bg-white border-b border-gray-200 overflow-x-auto scrollbar-none">
        <div className="flex gap-1 px-3 py-2 min-w-max">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const isActive = activeSection === s.id;
            return (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition ${
                  isActive
                    ? "bg-violet-100 text-violet-700"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {s.title}
              </button>
            );
          })}
        </div>
      </nav>

      <div className="max-w-7xl mx-auto flex">
        {/* ---- SIDEBAR (desktop) ---- */}
        <aside className="hidden lg:block w-64 shrink-0 sticky top-[57px] h-[calc(100vh-57px)] overflow-y-auto bg-white border-r border-gray-200 py-6 px-3">
          <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold px-3 mb-4">
            Navegação
          </p>
          <nav className="space-y-1">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const isActive = activeSection === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition text-left ${
                    isActive
                      ? "bg-violet-100 text-violet-700"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  {s.title}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* ---- MAIN CONTENT ---- */}
        <main className="flex-1 min-w-0 px-4 sm:px-8 py-8 space-y-16">
          {/* Description */}
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <p className="text-gray-600 max-w-2xl mb-6">
              Documento de especificação técnica completa da plataforma eBuz. Cobre
              requisitos funcionais e não-funcionais, arquitetura, modelo de dados,
              contratos de API, integracoes, deploy e testes.
            </p>
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {STATS.map((s) => (
                <div
                  key={s.label}
                  className="bg-violet-50 border border-violet-200 rounded-xl p-4 text-center"
                >
                  <p className="text-2xl font-bold text-violet-700">{s.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </motion.div>


          {/* =========== SECTION 0: LIVRO APE =========== */}
          <section
            ref={(el) => { sectionRefs.current["livro-ape"] = el; }}
            id="livro-ape"
            className="scroll-mt-28"
          >
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <BookOpen className="w-6 h-6 text-violet-600" /> Livro APE - Arquitetura de Produtos Educacionais
              </h2>
              <p className="text-gray-600 mb-6">O livro que fundamenta toda a plataforma eBuz. 50 anos de pesquisa científica de 12 pesquisadores condensados em um framework prático de engenharia de transferencia de conhecimento.</p>

              <div className="bg-violet-50 border border-violet-200 rounded-xl p-6 mb-6">
                <h3 className="font-bold text-violet-800 mb-3">Framework Universal</h3>
                <div className="flex flex-wrap items-center gap-2 text-sm font-mono">
                  {["INPUT", "GATEKEEPER", "BLACK BOX", "OUTPUT", "FEEDBACK"].map((node, idx) => (
                    <span key={node}>
                      <span className="px-3 py-1 bg-violet-100 rounded-lg text-violet-700 font-semibold">{node}</span>
                      {idx < 4 && <span className="text-violet-400 mx-1">&rarr;</span>}
                    </span>
                  ))}
                </div>
              </div>

              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 font-semibold text-gray-700 w-40">Pesquisador</th>
                      <th className="text-left p-3 font-semibold text-gray-700">Contribuição</th>
                      <th className="text-left p-3 font-semibold text-gray-700 w-32">No do Framework</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[
                      ["Michael Polanyi", "Conhecimento tácito - externalizar o que o expert sabe mas não consegue dizer", "INPUT"],
                      ["Malcolm Knowles", "Andragogia - 6 pressupostos de como adultos aprendem", "INPUT"],
                      ["Wiggins & McTighe", "Backward Design - comecar pelo OUTPUT, não pelo conteúdo", "OUTPUT"],
                      ["Benjamin Bloom", "Taxonomia cognitiva - 6 níveis hierarquicos de aprendizagem", "BLACK BOX"],
                      ["Robert Gagne", "9 eventos instrucionais - sequência confiavel para produzir aprendizagem", "BLACK BOX"],
                      ["K. Anders Ericsson", "Prática deliberada - foco, feedback, adaptacao, desconforto", "BLACK BOX"],
                      ["John Sweller", "Carga cognitiva - intrinseca, extrinseca, germinal", "BLACK BOX"],
                      ["William Glasser", "Piramide de retenção - modalidades de 10% a 95%", "BLACK BOX"],
                      ["Eugene Schwartz", "5 níveis de consciencia do comprador", "GATEKEEPER"],
                      ["Neil Rackham", "SPIN Selling - qualificação diagnostica", "GATEKEEPER"],
                      ["Lincoln Murphy", "Customer Success - Desired Outcome", "FEEDBACK"],
                      ["Fred Reichheld", "NPS - métrica que prediz crescimento sustentável", "FEEDBACK"],
                    ].map(([name, contrib, node]) => (
                      <tr key={name}>
                        <td className="p-3 font-medium text-gray-900 whitespace-nowrap">{name}</td>
                        <td className="p-3 text-gray-600">{contrib}</td>
                        <td className="p-3"><span className="px-2 py-0.5 bg-violet-100 text-violet-700 rounded text-xs font-semibold">{node}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { stat: "12", label: "Pesquisadores" },
                  { stat: "50+", label: "Anos de pesquisa" },
                  { stat: "5", label: "Nos do framework" },
                  { stat: "14", label: "Capitulos" },
                ].map((s) => (
                  <div key={s.label} className="p-3 rounded-lg border border-gray-200 bg-white text-center">
                    <p className="text-xl font-bold text-violet-600">{s.stat}</p>
                    <p className="text-xs text-gray-500">{s.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </section>


          {/* =========== SECTION: CURSOS =========== */}
          <section
            ref={(el) => { sectionRefs.current["cursos"] = el; }}
            id="cursos"
            className="scroll-mt-28"
          >
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <GraduationCap className="w-6 h-6 text-violet-600" /> Cursos Publicados
              </h2>
              <p className="text-gray-600 mb-6">8 cursos publicados (+ 1 em rascunho) na plataforma com player estilo Spotify, progresso por aula, estrelas e comentários. 9 cursos, 70 módulos, 362 aulas no total. Áudio gerado via Google TTS Chirp3-HD (voz masculina premium).</p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 font-semibold text-gray-700">Curso</th>
                      <th className="text-left p-3 font-semibold text-gray-700">Módulos</th>
                      <th className="text-left p-3 font-semibold text-gray-700">Aulas</th>
                      <th className="text-left p-3 font-semibold text-gray-700">Áudio</th>
                      <th className="text-left p-3 font-semibold text-gray-700">Leitura</th>
                      <th className="text-left p-3 font-semibold text-gray-700">Exercícios</th>
                      <th className="text-left p-3 font-semibold text-gray-700">Total</th>
                      <th className="text-left p-3 font-semibold text-gray-700">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[
                      { nome: "Arquitetura de Produtos Educacionais", modulos: "12", aulas: "67", audio: "11h52", leitura: "7h30", exercicios: "5h35", total: "25h", status: "Publicado" },
                      { nome: "Vibecoding PRO", modulos: "8", aulas: "46", audio: "3h19", leitura: "4h20", exercicios: "3h40", total: "11h19", status: "Publicado" },
                      { nome: "Pré-Vibecoding", modulos: "7", aulas: "37", audio: "2h22", leitura: "1h32", exercicios: "2h30", total: "6h24", status: "Publicado" },
                      { nome: "Pré-SEO/GEO", modulos: "7", aulas: "32", audio: "-", leitura: "-", exercicios: "-", total: "-", status: "Publicado" },
                      { nome: "SEO/GEO Pro", modulos: "10", aulas: "42", audio: "-", leitura: "-", exercicios: "-", total: "-", status: "Publicado" },
                      { nome: "Funil Evergreen - Fundamentos", modulos: "6", aulas: "35", audio: "-", leitura: "-", exercicios: "-", total: "-", status: "Publicado" },
                      { nome: "Funil Evergreen - Prática", modulos: "6", aulas: "37", audio: "-", leitura: "-", exercicios: "-", total: "-", status: "Publicado" },
                      { nome: "Metodo Low Ticket", modulos: "10", aulas: "55", audio: "-", leitura: "-", exercicios: "-", total: "-", status: "Publicado" },
                      { nome: "Método M.Ã.O.S.", modulos: "4", aulas: "21", audio: "-", leitura: "-", exercicios: "-", total: "-", status: "Rascunho" },
                    ].map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="p-3 font-medium text-gray-900">{row.nome}</td>
                        <td className="p-3 text-center">{row.modulos}</td>
                        <td className="p-3 text-center">{row.aulas}</td>
                        <td className="p-3 text-gray-600">{row.audio}</td>
                        <td className="p-3 text-gray-600">{row.leitura}</td>
                        <td className="p-3 text-gray-600">{row.exercicios}</td>
                        <td className="p-3 font-semibold text-violet-600">{row.total}</td>
                        <td className="p-3"><span className={`px-2 py-1 rounded-full text-xs font-medium ${row.status === "Rascunho" ? "bg-yellow-100 text-yellow-700" : "bg-green-100 text-green-700"}`}>{row.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400 mb-4">Áudio medido via ffprobe (duração real dos MP3). Leitura estimada a 200 palavras/min sobre o conteúdo das aulas. Exercícios estimados em 5 min/aula.</p>

              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {[
                  { stat: "9", label: "Cursos" },
                  { stat: "70", label: "Módulos" },
                  { stat: "362", label: "Aulas" },
                  { stat: "-", label: "Carga Horária Total" },
                  { stat: "Chirp3-HD", label: "Engine de Áudio" },
                ].map((s) => (
                  <div key={s.label} className="p-3 rounded-lg border border-gray-200 bg-white text-center">
                    <p className="text-xl font-bold text-violet-600">{s.stat}</p>
                    <p className="text-xs text-gray-500">{s.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </section>

          {/* =========== SECTION: ENTREVISTA JHOW =========== */}
          <section
            ref={(el) => { sectionRefs.current["entrevista"] = el; }}
            id="entrevista"
            className="scroll-mt-28"
          >
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-violet-600" /> Entrevista Estruturada com Jhow
              </h2>
              <p className="text-gray-600 mb-6">Chat interativo com personalidade Jhow (IA) que extrai o conhecimento tacito do expert em 4 fases progressivas com guardrails automaticos de progressao. Suporte a audio (transcricao via Groq Whisper) e upload de arquivos de referencia. Salva expert_summary no perfil do usuario ao concluir.</p>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {[
                  { fase: "1. Onboarding", desc: "Calibracao do expert (origem do lead, nivel de experiencia, se ja tem produto/tema/alunos)" },
                  { fase: "2. Orientacao", desc: "Explica o Passaporte de Contexto e verifica prontidao para comecar" },
                  { fase: "3. Entrevista Focada", desc: "Perguntas-chave para extrair: o que ensina, habilidades, publico-alvo, dor aguda e visao de esteira" },
                  { fase: "4. Conclusao + Handoff", desc: "Consolida resumo estruturado, gera expert_summary e documento final com seeding para proximos fluxos" },
                ].map((f) => (
                  <div key={f.fase} className="bg-violet-50 border border-violet-200 rounded-xl p-4">
                    <h4 className="font-bold text-violet-800 mb-2">{f.fase}</h4>
                    <p className="text-sm text-gray-600">{f.desc}</p>
                  </div>
                ))}
              </div>

              <SectionLabel>Dados Extraidos (extracted_data + expert_summary)</SectionLabel>
              <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" /> <span><strong>Origem do lead:</strong> como o expert chegou a plataforma (webinario, indicacao, contato direto)</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" /> <span><strong>Calibracao:</strong> nivel do expert (iniciante, intermediario, avancado)</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" /> <span><strong>O que ensina:</strong> conhecimento e habilidades transferidos</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" /> <span><strong>Habilidades:</strong> capacidades praticas desenvolvidas</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" /> <span><strong>Publico-alvo + Dor aguda:</strong> para quem resolve problema real</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" /> <span><strong>Visao de esteira:</strong> potencial de produtos derivados (10 ativos)</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" /> <span><strong>Expert Summary:</strong> salvo em profiles.expert_summary (nicho, experiencia, publico, transformacao, frase de impacto)</span></li>
                </ul>
              </div>

              <SectionLabel>Guardrails de Progressao</SectionLabel>
              <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" /> <span>Onboarding: max 8 mensagens, avanca ao extrair origemLead + calibracao</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" /> <span>Orientacao: max 6 mensagens, avanca quando prontoParaComecar = true</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" /> <span>Entrevista: avanca com 4 campos extraidos + min 12 mensagens (5 do usuario)</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" /> <span>Conclusao forcada: 30+ mensagens ou pedido explicito do usuario</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" /> <span>Progressao linear: nunca regride de fase (mapeia fases legadas automaticamente)</span></li>
                </ul>
              </div>

              <SectionLabel>Stack Tecnica</SectionLabel>
              <div className="flex flex-wrap gap-2 text-xs font-mono">
                {["interview-engine.ts (4 fases)", "jhow-prompt.ts (prompts por fase)", "ai-router.ts (fallback multi-LLM)", "7 endpoints /api/interview/*", "interview_sessions (RLS)", "profiles.expert_summary"].map((t) => (
                  <span key={t} className="px-3 py-1 bg-gray-100 rounded-lg text-gray-600">{t}</span>
                ))}
              </div>
            </motion.div>
          </section>

          {/* =========== SECTION: GERADOR DE PASSAPORTE =========== */}
          <section
            ref={(el) => { sectionRefs.current["passaporte-gen"] = el; }}
            id="passaporte-gen"
            className="scroll-mt-28"
          >
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <MapPin className="w-6 h-6 text-violet-600" /> Gerador de Passaporte
              </h2>
              <p className="text-gray-600 mb-6">Ferramenta de IA que gera um Passaporte de Contexto completo a partir do conhecimento do expert. Aceita texto, PDF ou entrevista com Jhow (IA). Gera passaporte + 6 segmentos de mercado + personas sob demanda.</p>

              <div className="space-y-4 mb-6">
                {[
                  { step: "1", title: "Input: Texto, PDF ou Entrevista Jhow", desc: "Expert fornece material (cole texto, upload PDF ou converse com Jhow IA)" },
                  { step: "2", title: "Passaporte: Geracao com IA", desc: "IA analisa e gera Passaporte de Contexto completo (consultor, segmento, persona, dores, conquistas, objecoes)" },
                  { step: "3", title: "Expansao: Segmentos + Personas", desc: "6 segmentos de mercado automaticos. Personas individuais sob demanda por segmento." },
                ].map((s) => (
                  <div key={s.step} className="flex items-start gap-4 bg-white border border-gray-200 rounded-xl p-4">
                    <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center shrink-0">
                      <span className="text-lg font-bold text-violet-600">{s.step}</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{s.title}</h4>
                      <p className="text-sm text-gray-600">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                {[
                  { stat: "3", label: "Etapas" },
                  { stat: "~30s", label: "Geracao" },
                  { stat: "6", label: "Segmentos" },
                  { stat: "Sob demanda", label: "Personas" },
                ].map((s) => (
                  <div key={s.label} className="p-3 rounded-lg border border-gray-200 bg-white text-center">
                    <p className="text-xl font-bold text-violet-600">{s.stat}</p>
                    <p className="text-xs text-gray-500">{s.label}</p>
                  </div>
                ))}
              </div>

              <SectionLabel>Stack Tecnica</SectionLabel>
              <div className="flex flex-wrap gap-2 text-xs font-mono">
                {["passaporte/prompts.ts", "passaporte/markdown-generator.ts", "ai-router.ts", "5 endpoints /api/passo1/*", "passaporte_projects (RLS)"].map((t) => (
                  <span key={t} className="px-3 py-1 bg-gray-100 rounded-lg text-gray-600">{t}</span>
                ))}
              </div>
            </motion.div>
          </section>


          {/* =========== SECTION: PLANOS =========== */}
          <section
            ref={(el) => { sectionRefs.current["planos"] = el; }}
            id="planos"
            className="scroll-mt-28"
          >
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Wallet className="w-6 h-6 text-violet-600" /> Planos
              </h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {PLANS.map((p, i) => (
                  <div
                    key={i}
                    className={`rounded-xl border p-5 text-center ${
                      p.highlight
                        ? "bg-violet-600 text-white border-violet-600 shadow-lg shadow-violet-200"
                        : "bg-white border-gray-200 shadow-sm"
                    }`}
                  >
                    <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${p.highlight ? "text-violet-200" : "text-violet-600"}`}>
                      {p.name}
                    </p>
                    <p className="text-3xl font-bold">
                      {p.price}
                      <span className={`text-sm font-normal ${p.highlight ? "text-violet-200" : "text-gray-400"}`}>
                        {p.period}
                      </span>
                    </p>
                    <p className={`text-sm mt-2 ${p.highlight ? "text-violet-100" : "text-gray-500"}`}>
                      {p.credits}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          </section>

          {/* =========== SECTION: PRECIFICACAO =========== */}
          <section
            ref={(el) => { sectionRefs.current["precificacao"] = el; }}
            id="precificacao"
            className="scroll-mt-28"
          >
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <CreditCard className="w-6 h-6 text-violet-600" /> Esteira de Produtos - Precificacao
              </h2>

              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-8">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Produto</th>
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Tipo</th>
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Preço</th>
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Modelo</th>
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Público</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {[
                      { produto: "Acesso Completo", tipo: "SaaS (plataforma)", preco: "R$ 19,90", modelo: "7 dias de acesso", publico: "Todos" },
                      { produto: "Pre-Vibecoding", tipo: "Curso online", preco: "R$ 47,00", modelo: "Acesso vitalicio", publico: "Iniciantes" },
                      { produto: "Vibecoding PRO", tipo: "Curso online", preco: "R$ 197,00", modelo: "Acesso vitalicio", publico: "Intermediarios" },
                      { produto: "Pre-SEO/GEO", tipo: "Curso online", preco: "Via sales_channel", modelo: "Acesso vitalicio", publico: "Iniciantes SEO" },
                      { produto: "SEO/GEO Pro", tipo: "Curso online", preco: "Via sales_channel", modelo: "Acesso vitalicio", publico: "Avancados SEO" },
                      { produto: "Funil Evergreen (2 cursos)", tipo: "Bundle (Fund. + Pratica)", preco: "R$ 49,90", modelo: "Acesso vitalicio", publico: "Profissionais de marketing" },
                      { produto: "Metodo Low Ticket", tipo: "Curso online", preco: "Via sales_channel", modelo: "Acesso vitalicio", publico: "Empreendedores" },
                      { produto: "Livro APE", tipo: "Livro fisico + digital", preco: "R$ 97,90", modelo: "Compra unica", publico: "Experts e educadores" },
                      { produto: "Maquina de Livros", tipo: "Servico editorial (4 pacotes)", preco: "R$ 750 a R$ 2.000", modelo: "Projeto unico", publico: "Autores" },
                      { produto: "APE (curso premium)", tipo: "Curso premium (67 aulas)", preco: "R$ 2.000,00", modelo: "Acesso vitalicio", publico: "Experts avancados" },
                    ].map((row, i) => (
                      <tr key={i} className="border-b border-gray-50 last:border-0">
                        <td className="px-4 py-2.5 font-medium text-gray-900">{row.produto}</td>
                        <td className="px-4 py-2.5">
                          <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-violet-100 text-violet-700">
                            {row.tipo}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`font-semibold ${row.preco === "Gratuito" ? "text-emerald-600" : "text-gray-900"}`}>
                            {row.preco}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">{row.modelo}</td>
                        <td className="px-4 py-2.5 text-gray-600">{row.publico}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <SectionLabel>Funil de Conversão</SectionLabel>
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 mb-6">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  {[
                    { label: "Captura (gratis)", color: "bg-emerald-50 border-emerald-200 text-emerald-700" },
                    { label: "Acesso (R$19,90)", color: "bg-blue-50 border-blue-200 text-blue-700" },
                    { label: "Cursos (R$47-197)", color: "bg-violet-50 border-violet-200 text-violet-700" },
                    { label: "Livro (R$97,90)", color: "bg-amber-50 border-amber-200 text-amber-700" },
                    { label: "Maquina Livros (R$750-2k)", color: "bg-orange-50 border-orange-200 text-orange-700" },
                    { label: "APE Premium (R$2.000)", color: "bg-red-50 border-red-200 text-red-700" },
                  ].map((step, si) => (
                    <div key={si} className="flex items-center gap-2">
                      <span className={`px-3 py-1.5 rounded-lg border font-medium ${step.color}`}>
                        {step.label}
                      </span>
                      {si < 5 && <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />}
                    </div>
                  ))}
                </div>
              </div>

              <SectionLabel>Arquitetura Comercial</SectionLabel>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 text-center">
                  <p className="text-2xl font-bold text-violet-600">products</p>
                  <p className="text-sm text-gray-500 mt-1">Registro unificado de produtos</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 text-center">
                  <p className="text-2xl font-bold text-emerald-600">sales_channels</p>
                  <p className="text-sm text-gray-500 mt-1">Slug-based routing + precos</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 text-center">
                  <p className="text-2xl font-bold text-amber-600">10 produtos</p>
                  <p className="text-sm text-gray-500 mt-1">Esteira completa</p>
                </div>
              </div>
            </motion.div>
          </section>


          {/* =========== SECTION: Funil Evergreen =========== */}
          <section
            ref={(el) => { sectionRefs.current["funil"] = el; }}
            id="funil"
            className="scroll-mt-28"
          >
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-violet-600" /> Funil Evergreen - Sistema de Vendas Automatizado
              </h2>

              <p className="text-gray-600 mb-6">
                Funil de 4 etapas que transforma trafego frio em alunos do curso APE. Operacao evergreen com webinario gravado de 45 minutos. Inclui 32 templates de email (5 sequencias), rastreamento Meta CAPI server-side e automacao WhatsApp pos-compra.
              </p>

              <SectionLabel>Páginas do Funil</SectionLabel>
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-8">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Página</th>
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Rota</th>
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Função</th>
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {[
                      { pagina: "Captura", rota: "/captura", funcao: "Landing page com formulário (nome + email). Entrada do funil via Meta Ads.", status: "Publicada" },
                      { pagina: "Webinário", rota: "/webinario", funcao: "Player do vídeo de 45 min + countdown + CTA para oferta", status: "Publicada" },
                      { pagina: "Oferta", rota: "/oferta", funcao: "Página de vendas do Curso APE. R$ 2.000 (12x R$ 197). 67 aulas, 12 módulos, garantia 7 dias.", status: "Publicada" },
                      { pagina: "Obrigado", rota: "/obrigado", funcao: "Confirmação pós-compra/registro. Próximos passos + acesso à área de membros.", status: "Publicada" },
                    ].map((row, i) => (
                      <tr key={i} className="border-b border-gray-50 last:border-0">
                        <td className="px-4 py-2.5 font-medium text-gray-900">{row.pagina}</td>
                        <td className="px-4 py-2.5">
                          <code className="text-xs bg-gray-100 px-2 py-0.5 rounded font-mono text-violet-700">{row.rota}</code>
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">{row.funcao}</td>
                        <td className="px-4 py-2.5">
                          <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-700">
                            {row.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <SectionLabel>Webinário APE - Roteiro Completo</SectionLabel>
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 mb-8">
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div className="space-y-2">
                    <p className="text-gray-700"><span className="font-semibold text-gray-900">Duração:</span> 45-50 minutos</p>
                    <p className="text-gray-700"><span className="font-semibold text-gray-900">Frameworks:</span> Duarte Resonate + StoryBrand + Phil M. Jones (P1-P5) + Nested Loops</p>
                    <p className="text-gray-700"><span className="font-semibold text-gray-900">Loops narrativos:</span> 3 loops integrados</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-gray-700"><span className="font-semibold text-gray-900">Slides:</span> 30 slides Canva (1920x1080, dark theme)</p>
                    <p className="text-gray-700"><span className="font-semibold text-gray-900">Demo ao vivo:</span> Framework dos 5 nós</p>
                    <p className="text-gray-700"><span className="font-semibold text-gray-900">Oferta:</span> Stack completo do curso</p>
                  </div>
                </div>
              </div>

              <SectionLabel>Fluxo do Funil</SectionLabel>
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 mb-8">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  {[
                    { label: "Meta Ads + CAPI", color: "bg-blue-50 border-blue-200 text-blue-700" },
                    { label: "Captura", color: "bg-emerald-50 border-emerald-200 text-emerald-700" },
                    { label: "Email (32 templates)", color: "bg-amber-50 border-amber-200 text-amber-700" },
                    { label: "Webinario", color: "bg-violet-50 border-violet-200 text-violet-700" },
                    { label: "Oferta", color: "bg-red-50 border-red-200 text-red-700" },
                    { label: "Checkout", color: "bg-orange-50 border-orange-200 text-orange-700" },
                    { label: "Obrigado", color: "bg-emerald-50 border-emerald-200 text-emerald-700" },
                    { label: "WhatsApp Auto", color: "bg-green-50 border-green-200 text-green-700" },
                    { label: "Area de Membros", color: "bg-violet-50 border-violet-200 text-violet-700" },
                  ].map((step, si) => (
                    <div key={si} className="flex items-center gap-2">
                      <span className={`px-3 py-1.5 rounded-lg border font-medium ${step.color}`}>
                        {step.label}
                      </span>
                      {si < 8 && <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />}
                    </div>
                  ))}
                </div>
              </div>

              <SectionLabel>Requisitos do Funil</SectionLabel>
              <div className="space-y-2">
                {[
                  { id: "RF-131", desc: "Pagina de captura com formulario de leads (/captura)" },
                  { id: "RF-132", desc: "Pagina do webinario com player e countdown (/webinario)" },
                  { id: "RF-133", desc: "Pagina de vendas com pricing e FAQ (/oferta)" },
                  { id: "RF-134", desc: "Pagina de obrigado com proximos passos (/obrigado)" },
                  { id: "RF-135", desc: "32 templates de email em 5 sequencias automaticas" },
                  { id: "RF-136", desc: "Meta CAPI tracking server-side" },
                  { id: "RF-137", desc: "Automacao WhatsApp pos-compra" },
                ].map((req, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg px-4 py-2.5 shadow-sm">
                    <span className="text-xs font-mono text-violet-600 font-semibold">{req.id}</span>
                    <span className="text-sm text-gray-700">{req.desc}</span>
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 ml-auto shrink-0" />
                  </div>
                ))}
              </div>
            </motion.div>
          </section>


          {/* =========== SECTION: Checkout Transparente =========== */}
          <section
            ref={(el) => { sectionRefs.current["checkout"] = el; }}
            id="checkout"
            className="scroll-mt-28"
          >
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <CreditCard className="w-6 h-6 text-violet-600" /> Checkout Transparente
              </h2>

              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 mb-8">
                <p className="text-gray-600 text-sm">
                  Checkout transparente via Assas (PIX, boleto, cartao com parcelas). Arquitetura baseada em sales_channels (slug-based routing) e products (registro unificado). Auto-create buyer para guest checkout. Multiplos endpoints especializados (transparent, credit-pack, book, fundamentos).
                </p>
              </div>

              <SectionLabel>Métodos de Pagamento</SectionLabel>
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-8">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Método</th>
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Fluxo</th>
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Confirmação</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {[
                      { metodo: "Cartão de Crédito", fluxo: "Dados inline \u2192 tokeniza \u2192 cobra", confirmacao: "Imediata" },
                      { metodo: "PIX", fluxo: "QR Code + copia-e-cola \u2192 polling 5s", confirmacao: "Tempo real (~30s)" },
                      { metodo: "Boleto", fluxo: "Código de barras + PDF", confirmacao: "Até 3 dias úteis" },
                    ].map((row, i) => (
                      <tr key={i} className="border-b border-gray-50 last:border-0">
                        <td className="px-4 py-2.5 font-medium text-gray-900">{row.metodo}</td>
                        <td className="px-4 py-2.5 text-gray-600">{row.fluxo}</td>
                        <td className="px-4 py-2.5">
                          <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-700">
                            {row.confirmacao}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <SectionLabel>Pontos de Integração</SectionLabel>
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-8">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Página</th>
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Produto</th>
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Valor</th>
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Parcelas</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {[
                      { pagina: "/oferta", produto: "Curso APE", valor: "R$ 2.000", parcelas: "12x" },
                      { pagina: "/planos", produto: "Acesso 7 dias", valor: "R$ 19,90", parcelas: "1x" },
                      { pagina: "/livro", produto: "Livro APE", valor: "R$ 97,90", parcelas: "3x" },
                      { pagina: "/creditos", produto: "Pacotes de creditos", valor: "Variavel", parcelas: "1x" },
                      { pagina: "/checkout/[slug]", produto: "Via sales_channel", valor: "Dinamico", parcelas: "Configuravel" },
                      { pagina: "/fundamentos", produto: "Modulo Fundamentos", valor: "R$ 49,90", parcelas: "1x" },
                    ].map((row, i) => (
                      <tr key={i} className="border-b border-gray-50 last:border-0">
                        <td className="px-4 py-2.5">
                          <code className="text-xs bg-gray-100 px-2 py-0.5 rounded font-mono text-violet-700">{row.pagina}</code>
                        </td>
                        <td className="px-4 py-2.5 font-medium text-gray-900">{row.produto}</td>
                        <td className="px-4 py-2.5 text-gray-700">{row.valor}</td>
                        <td className="px-4 py-2.5 text-gray-700">{row.parcelas}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <SectionLabel>Arquitetura</SectionLabel>
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 mb-8">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  {[
                    { label: "API Transparente Assas", color: "bg-violet-50 border-violet-200 text-violet-700" },
                    { label: "sales_channels (slug routing)", color: "bg-blue-50 border-blue-200 text-blue-700" },
                    { label: "products (registro unificado)", color: "bg-emerald-50 border-emerald-200 text-emerald-700" },
                    { label: "Auto-create buyer (guest)", color: "bg-amber-50 border-amber-200 text-amber-700" },
                    { label: "Webhook Assas", color: "bg-orange-50 border-orange-200 text-orange-700" },
                  ].map((badge, bi) => (
                    <span key={bi} className={`px-3 py-1.5 rounded-lg border font-medium ${badge.color}`}>
                      {badge.label}
                    </span>
                  ))}
                </div>
              </div>

              <SectionLabel>Segurança</SectionLabel>
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 mb-8">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                  {[
                    "Rate limiting por usuário e IP",
                    "Validação de CPF",
                    "Sanitização de inputs",
                    "HTTPS obrigatório",
                    "Sem secrets no código cliente",
                  ].map((item, si) => (
                    <div key={si} className="flex items-center gap-2 text-gray-700">
                      <Shield className="w-4 h-4 text-emerald-500 shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <SectionLabel>Requisitos do Checkout</SectionLabel>
              <div className="space-y-2">
                {[
                  { id: "RF-138", desc: "Checkout transparente com cartao inline (PIX, boleto, cartao com parcelas)" },
                  { id: "RF-139", desc: "Arquitetura sales_channels com slug-based routing" },
                  { id: "RF-140", desc: "Tabela products como registro unificado de produtos" },
                  { id: "RF-141", desc: "Auto-create buyer para guest checkout (sem cadastro previo)" },
                  { id: "RF-142", desc: "Multiplos endpoints: transparent, credit-pack, book, fundamentos" },
                  { id: "RF-143", desc: "Rate limiting por usuario e IP + validacao de CPF" },
                ].map((req, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg px-4 py-2.5 shadow-sm">
                    <span className="text-xs font-mono text-violet-600 font-semibold">{req.id}</span>
                    <span className="text-sm text-gray-700">{req.desc}</span>
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 ml-auto shrink-0" />
                  </div>
                ))}
              </div>
            </motion.div>
          </section>

          {/* =========== SECTION 1: VISAO =========== */}
          <section
            ref={(el) => { sectionRefs.current["visao"] = el; }}
            id="visao"
            className="scroll-mt-28"
          >
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Eye className="w-6 h-6 text-violet-600" /> Visão do Produto
              </h2>

              <SectionLabel>Proposta de Valor</SectionLabel>
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {VALUE_PROPS.map((v, i) => {
                  const Icon = v.icon;
                  return (
                    <div key={i} className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-violet-600" />
                        </div>
                        <h3 className="font-semibold text-gray-900 text-sm">{v.title}</h3>
                      </div>
                      <p className="text-gray-500 text-sm">{v.desc}</p>
                    </div>
                  );
                })}
              </div>

              <SectionLabel>Modelo de Negócio</SectionLabel>
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-8">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Item</th>
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Detalhes</th>
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Provedor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {BUSINESS_MODEL.map((b, i) => (
                      <tr key={i} className="border-b border-gray-50 last:border-0">
                        <td className="px-4 py-2.5 font-medium text-gray-900">{b.item}</td>
                        <td className="px-4 py-2.5 text-gray-500">{b.detail}</td>
                        <td className="px-4 py-2.5">
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-medium">
                            {b.provider}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <SectionLabel>Planos</SectionLabel>
              <div className="grid sm:grid-cols-3 gap-4">
                {PLANS.map((p, i) => (
                  <div
                    key={i}
                    className={`rounded-xl border p-5 text-center ${
                      p.highlight
                        ? "bg-violet-600 text-white border-violet-600 shadow-lg shadow-violet-200"
                        : "bg-white border-gray-200 shadow-sm"
                    }`}
                  >
                    <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${p.highlight ? "text-violet-200" : "text-violet-600"}`}>
                      {p.name}
                    </p>
                    <p className="text-3xl font-bold">
                      {p.price}
                      <span className={`text-sm font-normal ${p.highlight ? "text-violet-200" : "text-gray-400"}`}>
                        {p.period}
                      </span>
                    </p>
                    <p className={`text-sm mt-2 ${p.highlight ? "text-violet-100" : "text-gray-500"}`}>
                      {p.credits}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          </section>

          {/* =========== SECTION 2: REQUISITOS FUNCIONAIS =========== */}
          <section
            ref={(el) => { sectionRefs.current["requisitos"] = el; }}
            id="requisitos"
            className="scroll-mt-28"
          >
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <ListChecks className="w-6 h-6 text-violet-600" /> Requisitos Funcionais
              </h2>

              <div className="space-y-3">
                {FUNCTIONAL_MODULES.map((mod, mi) => {
                  const Icon = mod.icon;
                  const open = expandedModules.has(mi);
                  return (
                    <div key={mi} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                      <button
                        onClick={() => toggleModule(mi)}
                        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                            <Icon className="w-4 h-4 text-violet-600" />
                          </div>
                          <div>
                            <span className="font-semibold text-gray-900 text-sm">
                              {mi + 1}. {mod.title}
                            </span>
                            <span className="ml-2 text-xs text-gray-400">{mod.range}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-medium">
                            {mod.items.length} implementados
                          </span>
                          {open ? (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      </button>
                      {open && (
                        <div className="border-t border-gray-100 px-5 py-3 space-y-2">
                          {mod.items.map((r) => (
                            <div key={r.id} className="flex items-start gap-2 text-sm">
                              <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                              <span className="font-mono text-xs text-gray-400 w-14 shrink-0">
                                {r.id}
                              </span>
                              <span className="text-gray-700">{r.desc}</span>
                              <span className="ml-auto text-xs bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded shrink-0">
                                Implementado
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-800 font-medium">
                {totalReqs}/{totalReqs} implementados | 0 removidos
              </div>
            </motion.div>
          </section>

          {/* =========== SECTION 3: NAO-FUNCIONAIS =========== */}
          <section
            ref={(el) => { sectionRefs.current["nao-funcionais"] = el; }}
            id="nao-funcionais"
            className="scroll-mt-28"
          >
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <ShieldCheck className="w-6 h-6 text-violet-600" /> Requisitos Não-Funcionais
              </h2>

              <div className="space-y-6">
                {NF_GROUPS.map((g, gi) => {
                  const Icon = g.icon;
                  return (
                    <div key={gi} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                      <div className="flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-100">
                        <Icon className="w-5 h-5 text-violet-600" />
                        <h3 className="font-semibold text-gray-900">{g.title}</h3>
                        <span className="ml-auto text-xs text-gray-400">{g.items.length} itens</span>
                      </div>
                      <div className="divide-y divide-gray-50">
                        {g.items.map((item) => (
                          <div key={item.id} className="px-5 py-3 flex items-start gap-3 text-sm">
                            <span className="font-mono text-xs text-gray-400 w-16 shrink-0">{item.id}</span>
                            <span className="font-medium text-gray-800">{item.desc}</span>
                            <span className="ml-auto text-xs text-gray-400 text-right shrink-0 max-w-[200px]">{item.detail}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </section>

          {/* =========== SECTION 4: ARQUITETURA =========== */}
          <section
            ref={(el) => { sectionRefs.current["arquitetura"] = el; }}
            id="arquitetura"
            className="scroll-mt-28"
          >
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Layers className="w-6 h-6 text-violet-600" /> Arquitetura
              </h2>

              <SectionLabel>Stack Técnica</SectionLabel>
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-8">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Camada</th>
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Tecnologia</th>
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Versão</th>
                    </tr>
                  </thead>
                  <tbody>
                    {STACK_TABLE.map((layer) =>
                      layer.items.map((item, i) => (
                        <tr key={`${layer.layer}-${i}`} className="border-b border-gray-50 last:border-0">
                          {i === 0 && (
                            <td
                              className="px-4 py-2.5 font-medium text-gray-900 align-top"
                              rowSpan={layer.items.length}
                            >
                              {layer.layer}
                            </td>
                          )}
                          <td className="px-4 py-2.5 text-gray-700">{item.tech}</td>
                          <td className="px-4 py-2.5">
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-mono">
                              {item.version}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <SectionLabel>Diagrama de Arquitetura</SectionLabel>
              <div className="bg-gray-900 rounded-xl p-6 mb-8 overflow-x-auto">
                <pre className="text-emerald-400 text-xs sm:text-sm font-mono whitespace-pre leading-relaxed">
{`USUARIO -> VERCEL (Next.js 15.5) -> SUPABASE (PostgreSQL, Auth, Storage, RLS, Vault)
                                  -> AI ROUTER (Claude -> Groq -> Gemini -> Ollama -> Cache)
                                  -> SERVICOS (Assas, Twilio, Redis)
                                  -> HETZNER VPS (Ollama, Backups, Monitor)`}
                </pre>
              </div>

              <SectionLabel>Engine (src/engine/)</SectionLabel>
              <div className="bg-gray-900 rounded-xl p-6 overflow-x-auto">
                <pre className="text-violet-300 text-xs sm:text-sm font-mono whitespace-pre leading-relaxed">
{`FLOW ENGINE
  engine.ts (orquestrador)
    |-- ai-router.ts (Multi-LLM fallback)
    |-- credit-manager.ts
    |-- quality-validator.ts
    |-- document-generator.ts
    |-- json-importer.ts
    |-- interview-engine.ts + jhow-prompt.ts
    |-- trial-manager.ts
    |-- sms-provider.ts
    +-- coupon-manager.ts`}
                </pre>
              </div>
            </motion.div>
          </section>

          {/* =========== SECTION 5: FLUXOS DE USUARIO =========== */}
          <section
            ref={(el) => { sectionRefs.current["fluxos"] = el; }}
            id="fluxos"
            className="scroll-mt-28"
          >
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <GitBranch className="w-6 h-6 text-violet-600" /> Fluxos de Usuário
              </h2>

              <div className="space-y-6">
                {USER_FLOWS.map((flow, fi) => (
                  <div key={fi} className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
                    <h3 className="font-semibold text-gray-900 mb-4">{flow.title}</h3>
                    <div className="flex flex-wrap items-center gap-2">
                      {flow.steps.map((step, si) => (
                        <div key={si} className="flex items-center gap-2">
                          <div className="flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-lg px-3 py-1.5">
                            <span className="w-5 h-5 rounded-full bg-violet-600 text-white text-xs font-bold flex items-center justify-center">
                              {si + 1}
                            </span>
                            <span className="text-sm text-gray-700">{step.label}</span>
                          </div>
                          {si < flow.steps.length - 1 && (
                            <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </section>

          {/* =========== SECTION 6: MODELO DE DADOS =========== */}
          <section
            ref={(el) => { sectionRefs.current["dados"] = el; }}
            id="dados"
            className="scroll-mt-28"
          >
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Database className="w-6 h-6 text-violet-600" /> Modelo de Dados
              </h2>

              <SectionLabel>Entidades Principais</SectionLabel>
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {ENTITIES.map((ent) => (
                  <div key={ent.table} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                    <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                      <Database className="w-4 h-4 text-violet-500" />
                      <span className="font-mono font-semibold text-sm text-gray-900">{ent.table}</span>
                    </div>
                    <div className="px-4 py-2 space-y-1">
                      {ent.fields.map((f) => (
                        <div key={f.name} className="flex items-center gap-2 text-xs">
                          <span className="font-mono text-gray-700 w-28 shrink-0">{f.name}</span>
                          <span className="text-gray-400">{f.type}</span>
                          {f.note && (
                            <span className="ml-auto text-violet-500 font-semibold">{f.note}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <SectionLabel>Políticas RLS</SectionLabel>
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="text-left px-4 py-2 font-semibold text-gray-700">Tabela</th>
                      <th className="text-center px-4 py-2 font-semibold text-gray-700">SELECT</th>
                      <th className="text-center px-4 py-2 font-semibold text-gray-700">INSERT</th>
                      <th className="text-center px-4 py-2 font-semibold text-gray-700">UPDATE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {RLS_TABLE.map((r) => (
                      <tr key={r.table} className="border-b border-gray-50 last:border-0">
                        <td className="px-4 py-2 font-mono text-xs text-gray-700">{r.table}</td>
                        <td className="px-4 py-2 text-center"><RlsBadge level={r.select} /></td>
                        <td className="px-4 py-2 text-center"><RlsBadge level={r.insert} /></td>
                        <td className="px-4 py-2 text-center"><RlsBadge level={r.update} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </section>

          {/* =========== SECTION 7: API CONTRACT =========== */}
          <section
            ref={(el) => { sectionRefs.current["api"] = el; }}
            id="api"
            className="scroll-mt-28"
          >
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <FileCode2 className="w-6 h-6 text-violet-600" /> API Contract
              </h2>

              <div className="space-y-4">
                {API_GROUPS.map((g, gi) => (
                  <div key={gi} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                    <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
                      <h3 className="font-semibold text-gray-900 text-sm">{g.title}</h3>
                    </div>
                    <div className="divide-y divide-gray-50">
                      {g.routes.map((r, ri) => (
                        <div key={ri} className="px-5 py-2.5 flex items-center gap-3 text-sm">
                          <MethodBadge method={r.method} />
                          <span className="font-mono text-gray-700 text-xs">{r.path}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </section>

          {/* =========== SECTION 8: INTEGRACOES =========== */}
          <section
            ref={(el) => { sectionRefs.current["integracoes"] = el; }}
            id="integracoes"
            className="scroll-mt-28"
          >
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Plug className="w-6 h-6 text-violet-600" /> Integracoes
              </h2>

              <div className="grid sm:grid-cols-2 gap-4">
                {INTEGRATIONS.map((intg, i) => {
                  const Icon = intg.icon;
                  return (
                    <div key={i} className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-violet-600" />
                        </div>
                        <h3 className="font-semibold text-gray-900 text-sm">{intg.title}</h3>
                      </div>
                      <ul className="space-y-1.5">
                        {intg.features.map((f, fi) => (
                          <li key={fi} className="text-sm text-gray-600 flex items-start gap-2">
                            <span className="text-violet-400 mt-1">-</span>
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </section>

          {/* =========== SECTION 9: DEPLOY =========== */}
          <section
            ref={(el) => { sectionRefs.current["deploy"] = el; }}
            id="deploy"
            className="scroll-mt-28"
          >
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Rocket className="w-6 h-6 text-violet-600" /> Plano de Deploy
              </h2>

              <SectionLabel>Ambientes</SectionLabel>
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Globe className="w-4 h-4 text-violet-500" /> Vercel (Produção)
                  </h3>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>Push para main = deploy automático</li>
                    <li>Package manager: pnpm</li>
                    <li>Framework: Next.js 15.5 (auto-detected)</li>
                    <li>Dominio: app.ebuz.com.br</li>
                    <li>Edge Network global</li>
                  </ul>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Server className="w-4 h-4 text-violet-500" /> Hetzner VPS
                  </h3>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>IP: 77.42.31.230</li>
                    <li>SSH porta 2847</li>
                    <li>Ollama (LLM local)</li>
                    <li>Backups automáticos</li>
                    <li>Monitor + alertas</li>
                  </ul>
                </div>
              </div>

              <SectionLabel>Variaveis de Ambiente</SectionLabel>
              <div className="bg-gray-900 rounded-xl p-6 mb-8 overflow-x-auto">
                <pre className="text-gray-300 text-xs font-mono whitespace-pre leading-relaxed">
                  {DEPLOY_ENV_VARS}
                </pre>
              </div>

              <SectionLabel>Protocolo de Deploy</SectionLabel>
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 mb-8">
                <ol className="space-y-2">
                  {DEPLOY_STEPS.map((step, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      <span className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center shrink-0">
                        {i + 1}
                      </span>
                      <span className="text-gray-700">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <SectionLabel>Migrations</SectionLabel>
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 mb-8">
                <div className="grid sm:grid-cols-2 gap-1">
                  {MIGRATIONS.map((m) => (
                    <div key={m} className="flex items-center gap-2 text-xs font-mono text-gray-600 py-1">
                      <FileText className="w-3 h-3 text-gray-400" />
                      {m}
                    </div>
                  ))}
                </div>
              </div>

              <SectionLabel>Monitoramento</SectionLabel>
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-start gap-2">
                    <Activity className="w-4 h-4 text-violet-500 mt-0.5 shrink-0" />
                    <span>Cron a cada 5 min: monitor-v2.sh (13 checks de saúde)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <BarChart3 className="w-4 h-4 text-violet-500 mt-0.5 shrink-0" />
                    <span>Daily audit as 6h (7 categorias: disco, memoria, CPU, certificados, backups, logs, serviços)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Radio className="w-4 h-4 text-violet-500 mt-0.5 shrink-0" />
                    <span>Health endpoint: GET /api/health (status de todos os serviços)</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </section>

          {/* =========== SECTION 10: TESTES =========== */}
          <section
            ref={(el) => { sectionRefs.current["testes"] = el; }}
            id="testes"
            className="scroll-mt-28"
          >
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <TestTube2 className="w-6 h-6 text-violet-600" /> Plano de Testes
              </h2>

              <div className="space-y-6 mb-8">
                {TEST_GROUPS.map((g, gi) => (
                  <div key={gi} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                    <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900 text-sm">{g.title}</h3>
                      <span className="text-xs text-gray-400">{g.items.length} itens</span>
                    </div>
                    <div className="divide-y divide-gray-50">
                      {g.items.map((item, i) => (
                        <div key={i} className="px-5 py-2.5 flex items-center justify-between text-sm">
                          <span className="text-gray-700 font-mono text-xs">{item.name}</span>
                          <PriorityBadge priority={item.priority} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <SectionLabel>Cenarios Criticos</SectionLabel>
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
                <ul className="space-y-2">
                  {CRITICAL_SCENARIOS.map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                      <span className="text-gray-700">{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </section>

          {/* =========== SECTION 13: ADRs =========== */}
          <section
            ref={(el) => { sectionRefs.current["adrs"] = el; }}
            id="adrs"
            className="scroll-mt-28"
          >
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <FileText className="w-6 h-6 text-violet-600" /> ADRs (Architecture Decision Records)
              </h2>

              <div className="space-y-3">
                {ADRS.map((adr, i) => {
                  const open = expandedAdrs.has(i);
                  return (
                    <div key={i} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                      <button
                        onClick={() => toggleAdr(i)}
                        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-mono text-gray-400">{adr.id}</span>
                          <span className="font-semibold text-gray-900 text-sm">{adr.title}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-medium">
                            {adr.status}
                          </span>
                          {open ? (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      </button>
                      {open && (
                        <div className="border-t border-gray-100 px-5 py-4 space-y-3 text-sm">
                          <div>
                            <p className="text-violet-600 uppercase tracking-wider text-xs font-semibold mb-1">
                              Contexto
                            </p>
                            <p className="text-gray-600">{adr.context}</p>
                          </div>
                          <div>
                            <p className="text-violet-600 uppercase tracking-wider text-xs font-semibold mb-1">
                              Decisão
                            </p>
                            <p className="text-gray-700 font-medium">{adr.decision}</p>
                          </div>
                          <div>
                            <p className="text-red-500 uppercase tracking-wider text-xs font-semibold mb-1">
                              Rejeitado
                            </p>
                            <p className="text-gray-500">{adr.rejected}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </section>

          {/* =========== SECTION: MAQUINA DE LIVROS =========== */}
          <section
            ref={(el) => { sectionRefs.current["maquina-livros"] = el; }}
            id="maquina-livros"
            className="scroll-mt-28"
          >
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <PenTool className="w-6 h-6 text-violet-600" /> Máquina de Livros
              </h2>

              <p className="text-gray-600 mb-6">
                Fabrica modular de producao editorial. Cada projeto usa apenas os modulos que precisa. Build types: preview, print, digital, epub, audiobook, kdp_package. Inclui Google TTS para audio de capitulos, gestao de capa e pedidos com frete.
              </p>

              {/* Modulos */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[
                  { nome: "Editor", desc: "Escrita capitulo a capitulo em Markdown", status: "Implementado" },
                  { nome: "Importacao", desc: "Colar texto ou upload de .md/.txt", status: "Implementado" },
                  { nome: "Revisao IA", desc: "Gramatica, editorial, acentuacao, anti-IA", status: "Implementado" },
                  { nome: "Build", desc: "preview, print, digital, epub, audiobook, kdp_package", status: "Implementado" },
                  { nome: "Capa", desc: "Gestao de capa do livro", status: "Implementado" },
                  { nome: "Audio TTS", desc: "Google TTS para capitulos em audio", status: "Implementado" },
                  { nome: "Pedidos", desc: "Book orders com frete", status: "Implementado" },
                  { nome: "Exportacao", desc: "PDF, EPUB, Markdown, KDP", status: "Implementado" },
                ].map((mod) => (
                  <div key={mod.nome} className="bg-white border border-gray-200 rounded-xl p-3">
                    <p className="font-semibold text-gray-900 text-sm">{mod.nome}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{mod.desc}</p>
                    <span className="inline-block mt-2 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-medium">
                      {mod.status}
                    </span>
                  </div>
                ))}
              </div>

              {/* Tabelas */}
              <h3 className="font-semibold text-gray-900 mb-3">Modelo de dados</h3>
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-3 py-2 font-medium text-gray-700">Tabela</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-700">Descrição</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-700">RLS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[
                      { tabela: "book_projects", desc: "Projetos de livro (titulo, autor, tipo, formato, estagio)", rls: "Sim" },
                      { tabela: "book_chapters", desc: "Capitulos (content_md, word_count, sort_order)", rls: "Sim" },
                      { tabela: "book_reviews", desc: "Revisoes IA (findings JSONB, tipo, severidade)", rls: "Sim" },
                      { tabela: "book_builds", desc: "Compilacoes (preview, print, digital, epub, audiobook, kdp_package)", rls: "Sim" },
                      { tabela: "book_checklists", desc: "QA items por capitulo (tipo, checked)", rls: "Sim" },
                      { tabela: "book_orders", desc: "Pedidos de livro com frete e endereco de entrega", rls: "Sim" },
                    ].map((row) => (
                      <tr key={row.tabela} className="bg-white">
                        <td className="px-3 py-2 font-mono text-xs text-violet-600">{row.tabela}</td>
                        <td className="px-3 py-2 text-gray-600">{row.desc}</td>
                        <td className="px-3 py-2 text-emerald-600 font-medium">{row.rls}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Planos comerciais */}
              <h3 className="font-semibold text-gray-900 mb-3">Planos comerciais</h3>
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-3 py-2 font-medium text-gray-700">Plano</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-700">Preço</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-700">Inclui</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[
                      { plano: "Livro Completo", preco: "R$ 2.000", inclui: "Escrita + Revisao + Diagramacao + Publicacao" },
                      { plano: "Revisao + Diagramacao", preco: "R$ 1.500", inclui: "Importacao + Revisao + Diagramacao + EPUB" },
                      { plano: "Diagramacao", preco: "R$ 1.000", inclui: "Importacao + Diagramacao + EPUB" },
                      { plano: "Publicacao", preco: "R$ 750", inclui: "EPUB + Audiolivro (Google TTS) + KDP Package" },
                    ].map((row) => (
                      <tr key={row.plano} className="bg-white">
                        <td className="px-3 py-2 font-semibold text-gray-900">{row.plano}</td>
                        <td className="px-3 py-2 text-gray-700">{row.preco}</td>
                        <td className="px-3 py-2 text-gray-600">{row.inclui}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Arquivos */}
              <h3 className="font-semibold text-gray-900 mb-3">Arquivos do sistema</h3>
              <div className="bg-gray-900 text-gray-100 rounded-xl p-4 text-xs font-mono overflow-x-auto">
                <pre>{`lib/book-engine/
  types.ts              # Tipos TypeScript (BookProject, BookChapter, etc.)
  accent-corrector.ts   # 200+ mapeamentos PT-BR
  anti-ia-scanner.ts    # 150+ padrões de vocabulário IA
  checklist-templates.ts # QA items por tipo de capítulo

src/routes/
  admin-livros-route.ts           # GET/POST projetos
  admin-livros-id-route.ts        # GET/PUT/DELETE projeto
  admin-livros-capitulos-route.ts # GET/POST capítulos
  admin-livros-capitulo-slug-route.ts # GET/PUT/DELETE capítulo
  admin-livros-build-route.ts     # POST build + GET histórico
  admin-livros-revisar-route.ts   # GET/POST/PUT revisões
  admin-livros-importar-route.ts  # POST importação
  admin-livros-exportar-route.ts  # GET/POST exportação

src/pages/
  admin-livros-dashboard-page.tsx # Dashboard com métricas
  admin-livros-novo-page.tsx      # Formulário novo projeto
  admin-livros-projeto-page.tsx   # Detalhe com módulos
  admin-livros-capitulo-page.tsx  # Editor Markdown
  admin-livros-revisao-page.tsx   # Revisão com tabs
  admin-livros-importar-page.tsx  # Importação (colar/upload)
  admin-livros-exportar-page.tsx  # Exportação (PDF/EPUB/MD)

app/maquina-livros/page.tsx       # Página de vendas pública

infra/supabase/migrations/
  034_maquina_livros.sql          # 5 tabelas + RLS + storage`}</pre>
              </div>
            </motion.div>
          </section>

          {/* =========== SECTION: GERADOR DE PASSAPORTES (PASSO 1) =========== */}
          <section
            ref={(el) => { sectionRefs.current["passo1"] = el; }}
            id="passo1"
            className="scroll-mt-28"
          >
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <FileCheck className="w-6 h-6 text-violet-600" /> Gerador de Passaportes (Passo 1)
              </h2>

              <p className="text-gray-600 mb-6">
                Geracao automatizada em 3 estagios: Passaporte (consultor, segmento, persona, crencas, dores, conquistas, objecoes, medos, solucoes, clientes) + 6 Segmentos de mercado + Personas sob demanda. Input via texto, PDF ou entrevista com Jhow. Output em PassaporteData JSON. Billing: ~40.000 moedas por passaporte, ~15.000 por persona.
              </p>

              {/* Fluxo de uso */}
              <h3 className="font-semibold text-gray-900 mb-3">Fluxo de geracao (3 estagios)</h3>
              <div className="space-y-4 mb-6">
                {[
                  { step: "1", title: "Input: texto, PDF ou entrevista Jhow", desc: "Expert cola texto, faz upload de PDF para extracao ou usa entrevista com Jhow IA" },
                  { step: "2", title: "Passaporte: geracao com IA", desc: "IA analisa conteudo e gera PassaporteData JSON completo (consultor, segmento, persona, dores, conquistas, objecoes)" },
                  { step: "3", title: "Expansao: segmentos + personas sob demanda", desc: "6 segmentos de mercado automaticos. Personas individuais geradas sob demanda por segmento selecionado." },
                ].map((s) => (
                  <div key={s.step} className="flex items-start gap-4 bg-white border border-gray-200 rounded-xl p-4">
                    <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center shrink-0">
                      <span className="text-lg font-bold text-violet-600">{s.step}</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{s.title}</h4>
                      <p className="text-sm text-gray-600">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Requisitos */}
              <h3 className="font-semibold text-gray-900 mb-3">Requisitos funcionais</h3>
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-6">
                <div className="divide-y divide-gray-50 px-5 py-3 space-y-2">
                  {[
                    { id: "RF-P1-001", desc: "Input multi-formato (texto, PDF, entrevista Jhow)" },
                    { id: "RF-P1-002", desc: "Geracao de Passaporte completo via IA (PassaporteData JSON)" },
                    { id: "RF-P1-003", desc: "Geracao automatica de 6 segmentos de mercado" },
                    { id: "RF-P1-004", desc: "Personas sob demanda por segmento selecionado" },
                    { id: "RF-P1-005", desc: "Billing: ~40.000 moedas/passaporte, ~15.000/persona" },
                    { id: "RF-P1-006", desc: "Few-shot learning de passaportes de referencia (alta qualidade)" },
                    { id: "RF-P1-007", desc: "Banco: passaporte_projects (RLS por usuario)" },
                    { id: "RF-P1-008", desc: "Foto de persona via /api/passo1/[slug]/photo" },
                  ].map((r) => (
                    <div key={r.id} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                      <span className="font-mono text-xs text-gray-400 w-20 shrink-0">{r.id}</span>
                      <span className="text-gray-700">{r.desc}</span>
                      <span className="ml-auto text-xs bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded shrink-0">
                        Implementado
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Arquitetura */}
              <h3 className="font-semibold text-gray-900 mb-3">Arquitetura</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[
                  { nome: "passaporte_projects", desc: "Tabela com RLS por usuário", status: "Implementado" },
                  { nome: "Flows virtuais", desc: "Agrupamento na biblioteca", status: "Implementado" },
                  { nome: "AI Router", desc: "Fallback Claude - Groq - Gemini", status: "Implementado" },
                  { nome: "Few-shot refs", desc: "Referências para aprendizado de formato", status: "Implementado" },
                ].map((mod) => (
                  <div key={mod.nome} className="bg-white border border-gray-200 rounded-xl p-3">
                    <p className="font-semibold text-gray-900 text-sm">{mod.nome}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{mod.desc}</p>
                    <span className="inline-block mt-2 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-medium">
                      {mod.status}
                    </span>
                  </div>
                ))}
              </div>

              {/* Temas visuais */}
              <h3 className="font-semibold text-gray-900 mb-3">Temas visuais</h3>
              <div className="flex flex-wrap gap-2 mb-6">
                {[
                  { nome: "Teal", cor: "bg-teal-500" },
                  { nome: "Emerald", cor: "bg-emerald-500" },
                  { nome: "Blue", cor: "bg-blue-500" },
                  { nome: "Violet", cor: "bg-violet-500" },
                  { nome: "Amber", cor: "bg-amber-500" },
                  { nome: "Rose", cor: "bg-rose-500" },
                ].map((t) => (
                  <div key={t.nome} className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
                    <div className={`w-4 h-4 rounded-full ${t.cor}`} />
                    <span className="text-sm text-gray-700 font-medium">{t.nome}</span>
                  </div>
                ))}
              </div>

              {/* Rotas */}
              <h3 className="font-semibold text-gray-900 mb-3">Rotas</h3>
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-3 py-2 font-medium text-gray-700">Rota</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-700">Descrição</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[
                      { rota: "/passo1", desc: "Upload + formulario de entrada" },
                      { rota: "/passo1/[slug]/passaporte", desc: "Visualizacao do Passaporte de Contexto" },
                      { rota: "/passo1/[slug]/segmentos", desc: "Visualizacao dos 6 Segmentos" },
                      { rota: "/api/passo1/generate", desc: "Geracao do passaporte via IA" },
                      { rota: "/api/passo1/[slug]/segments", desc: "Geracao dos 6 segmentos de mercado" },
                      { rota: "/api/passo1/[slug]/persona", desc: "Geracao de persona sob demanda por segmento" },
                      { rota: "/api/passo1/[slug]/photo", desc: "Geracao de foto de persona" },
                      { rota: "/api/passo1/extract-pdf", desc: "Extracao de texto de PDF" },
                    ].map((row) => (
                      <tr key={row.rota} className="bg-white">
                        <td className="px-3 py-2 font-mono text-xs text-violet-600">{row.rota}</td>
                        <td className="px-3 py-2 text-gray-600">{row.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Arquivos */}
              <h3 className="font-semibold text-gray-900 mb-3">Arquivos do sistema</h3>
              <div className="bg-gray-900 text-gray-100 rounded-xl p-4 text-xs font-mono overflow-x-auto">
                <pre>{`app/
  passo1/page.tsx                    # Upload + formulario
  passo1/[slug]/passaporte/page.tsx  # Documento Passaporte
  passo1/[slug]/segmentos/page.tsx   # Documento Segmentos

app/api/passo1/
  generate/route.ts                  # Geracao do passaporte
  [slug]/segments/route.ts           # Geracao dos 6 segmentos
  [slug]/persona/route.ts            # Persona sob demanda
  [slug]/photo/route.ts              # Foto de persona
  extract-pdf/route.ts               # Extracao de PDF

lib/passaporte/
  prompts.ts                         # Prompts para geracao
  markdown-generator.ts              # Gerador de markdown estruturado
  references.ts                      # Few-shot refs (alta qualidade)

lib/
  ai-router.ts                       # Fallback multi-LLM
  slug-generator.ts                  # Gerador de slug inteligente

DB: passaporte_projects (RLS)`}</pre>
              </div>
            </motion.div>
          </section>

          {/* =========== SECTION: AUTH / ONBOARDING =========== */}
          <section
            ref={(el) => { sectionRefs.current["auth-onboarding"] = el; }}
            id="auth-onboarding"
            className="scroll-mt-28"
          >
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Key className="w-6 h-6 text-violet-600" /> Auth / Onboarding
              </h2>

              <p className="text-gray-600 mb-6">
                Sistema completo de autenticacao com email/senha + Google OAuth, trial 24h via SMS (Twilio), multi-tenant orgs (auto-created), auto-create buyer para compras via webhook Assas, hierarquia admin (super_admin - admin - user), middleware com cache Redis e honeypot anti-bot.
              </p>

              {/* Requisitos */}
              <h3 className="font-semibold text-gray-900 mb-3">Requisitos funcionais</h3>
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-6">
                <div className="divide-y divide-gray-50 px-5 py-3 space-y-2">
                  {[
                    { id: "RF-AUTH-001", desc: "Email/senha + Google OAuth" },
                    { id: "RF-AUTH-002", desc: "Trial 24h via SMS (Twilio) com anti-fraude (1 chip = 1 trial)" },
                    { id: "RF-AUTH-003", desc: "Multi-tenant orgs (auto-created no cadastro)" },
                    { id: "RF-AUTH-004", desc: "Auto-create buyer (webhook Assas para guest checkout)" },
                    { id: "RF-AUTH-005", desc: "Hierarquia admin: super_admin > admin > user" },
                    { id: "RF-AUTH-006", desc: "Middleware route protection com cache Redis" },
                    { id: "RF-AUTH-007", desc: "Honeypot anti-bot no formulario de cadastro" },
                    { id: "RF-AUTH-008", desc: "SMTP customizado (noreply@ebuz.com.br)" },
                    { id: "RF-AUTH-009", desc: "Criacao manual de usuario pelo admin" },
                    { id: "RF-AUTH-010", desc: "Password reset via Admin API" },
                  ].map((r) => (
                    <div key={r.id} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                      <span className="font-mono text-xs text-gray-400 w-24 shrink-0">{r.id}</span>
                      <span className="text-gray-700">{r.desc}</span>
                      <span className="ml-auto text-xs bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded shrink-0">
                        Implementado
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Detalhes */}
              <h3 className="font-semibold text-gray-900 mb-3">Detalhes de implementacao</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                {[
                  { nome: "Email/senha + Google OAuth", desc: "Login via Supabase Auth com email/senha ou Google OAuth", status: "Implementado" },
                  { nome: "Trial 24h via SMS", desc: "Twilio SMS com codigo 6 digitos. Anti-fraude: hash SHA-256, rate limiting, 1 chip = 1 trial", status: "Implementado" },
                  { nome: "Multi-tenant workspaces", desc: "Workspaces auto-created no cadastro. Cada usuario pertence a um workspace.", status: "Implementado" },
                  { nome: "Auto-create buyer", desc: "Webhook Assas cria usuario automaticamente para guest checkout (sem cadastro previo)", status: "Implementado" },
                  { nome: "Admin hierarchy", desc: "super_admin > admin > user. Middleware verifica role em rotas protegidas.", status: "Implementado" },
                  { nome: "Redis middleware cache", desc: "Cache de sessao e verificacao de acesso via Redis Upstash", status: "Implementado" },
                  { nome: "Honeypot anti-bot", desc: "Campo invisivel no formulario de cadastro para detectar bots", status: "Implementado" },
                  { nome: "SMTP + criacao manual", desc: "SMTP customizado noreply@ebuz.com.br. Admin cria usuarios com trial opcional.", status: "Implementado" },
                ].map((mod) => (
                  <div key={mod.nome} className="bg-white border border-gray-200 rounded-xl p-3">
                    <p className="font-semibold text-gray-900 text-sm">{mod.nome}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{mod.desc}</p>
                    <span className="inline-block mt-2 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-medium">
                      {mod.status}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </section>

          {/* FOOTER */}
          <div className="border-t border-gray-200 pt-8 pb-4 text-center text-xs text-gray-400">
            eBuz Negócios Digitais - SDD v1.0 - Marco 2026
          </div>
        </main>
      </div>
    </div>
  );
}
